import React, { useState, useRef, useEffect } from 'react';
import { vendorInvoicesAPI, bankAPI, departmentsAPI, financialPowersAPI } from '../services/api';
import { useApi, usePaginatedApi } from '../hooks/useApi';
import { VendorInvoice, User, VendorInvoiceStats } from '../types';
import { formatCurrency, getCurrencyFontClass } from '../utils/currency';
import VendorProgressChart from '../components/VendorProgressChart';
import Pagination from '../components/Pagination';
import Spinner from '../components/Spinner';
import { useAuth } from '../contexts/AuthContext';
import {
  Search,
  FileText,
  Edit,
  AlertCircle,
  Upload,
  Check,
  X,
  Clock,
  Trash2,
  Eye,
  Calendar,
  Building,
  ChevronDown,
  Download,
  Plus
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { errorModal } from '../components/ErrorModal';
import { confirmToast } from '../components/confirmToast';
import { useNavigate } from 'react-router-dom';
import { decryptData } from '../utils/encryption';

interface ValidationErrors {
  vendor_id?: string;
  invoice_number?: string;
  invoice_date?: string;
  due_date?: string;
  total_amount?: string;
  subtotal?: string;
  tax_amount?: string;
  po_number?: string;
  description?: string;
  file?: string;
  department_id?: string;
  registration_number?: string;
  approval_type?: string;
  proposal?: string;
}

interface FinancialPowerDropdownItem {
  id: number;
  vendorName: string;
  referenceNumber: string;
  referenceName: string;
}

const Invoices: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const userPermissionsLocalStorage = localStorage.getItem('permissions') || '';
  const userPermissions = decryptData(userPermissionsLocalStorage);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showChartModal, setShowChartModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<VendorInvoice | null>(null);
  const [auditTrailData, setAuditTrailData] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAttachmentViewer, setShowAttachmentViewer] = useState(false);
  const [currentAttachmentUrl, setCurrentAttachmentUrl] = useState<string>('');
  const [currentAttachmentName, setCurrentAttachmentName] = useState<string>('');
  const [loadingAttachment, setLoadingAttachment] = useState(false);
  const [originalAttachmentUrl, setOriginalAttachmentUrl] = useState<string>('');

  // Searchable dropdown states
  const departmentDropdownRef = useRef<HTMLDivElement>(null);

  // Approval type dropdown states
  const [approvalTypeDropdownOpen, setApprovalTypeDropdownOpen] = useState(false);
  const approvalTypeDropdownRef = useRef<HTMLDivElement>(null);

  const approvalTypeOptions = [
    'Invoice Payment - Financial approval available',
    'Financial approval + Invoice Payment'
  ];

  // Financial powers dropdown states
  const [financialPowersDropdown, setFinancialPowersDropdown] = useState<FinancialPowerDropdownItem[]>([]);
  const [loadingFinancialPowers, setLoadingFinancialPowers] = useState(false);
  const [proposalDropdownOpen, setProposalDropdownOpen] = useState(false);
  const [proposalSearchTerm, setProposalSearchTerm] = useState('');
  const proposalDropdownRef = useRef<HTMLDivElement>(null);

  // Approver notes (per level) used when acting as approver
  const [approverNotes, setApproverNotes] = useState<{ 1: string; 2: string; 3: string }>({ 1: '', 2: '', 3: '' });
  // Dynamic approver notes (keyed by approver_id)
  const [dynamicApproverNotes, setDynamicApproverNotes] = useState<{ [key: number]: string }>({});
  // Track which specific approval action is being submitted: "approverId-action" or "level-action"
  const [submittingApproval, setSubmittingApproval] = useState<string | null>(null);
  const [financeForm, setFinanceForm] = useState<Array<{ account_number: string; account_name: string; amount: string; narration: string }>>([
    { account_number: '', account_name: '', amount: '', narration: '' }
  ]);
  const [financeErrors, setFinanceErrors] = useState<Array<{ account_number?: string; account_name?: string; amount?: string; narration?: string }>>([{}]);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { data: invoices, pagination, loading, error, refetch } = usePaginatedApi<VendorInvoice>(
    () => vendorInvoicesAPI.getAll({ page, limit, search: searchTerm, status: statusFilter }),
    [page, limit, searchTerm, statusFilter]
  );


  const { data: invoiceApprovers } = useApi<User[]>(
    () => vendorInvoicesAPI.invoiceApproverDropdown(),
    []
  );

  // Stats for invoices
  const { data: invoiceStats } = useApi<VendorInvoiceStats>(
    () => vendorInvoicesAPI.getStats(),
    []
  );


  const [uploadForm, setUploadForm] = useState({
    department_id: '',
    vendor_id: '',
    invoice_number: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: '',
    total_amount: '',
    subtotal: '',
    tax_amount: '',
    description: '',
    notes: '',
    po_number: '',
    registration_number: '',
    use_ocr: false,
    approver_1: '',
    approver_2: '',
    approval_type: '',
    proposal: '',
    file: null as File | null
  });

  const [statusForm, setStatusForm] = useState({
    status: '',
    notes: ''
  });

  const [paymentForm, setPaymentForm] = useState({
    paid_amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    notes: '',
    reference_number: '',
    broucher: null as File | null
  });

  const [paymentErrors, setPaymentErrors] = useState<{ reference_number?: string }>({});

  const [paymentsMap, setPaymentsMap] = useState<{ [invoiceId: number]: { amount: number, date: string }[] }>({});

  // Bank selection/creation state for status approval
  const [bankOptions, setBankOptions] = useState<Array<{ id: number; bank_name: string; account_holder?: string; account_number?: string; account_type?: string }>>([]);
  const [selectedBankId, setSelectedBankId] = useState<string>('');
  const [showAddBank, setShowAddBank] = useState<boolean>(false);
  const [addBankForm, setAddBankForm] = useState({
    bank_name: '',
    account_holder_name: '',
    account_number: '',
    account_type: '',
    ifsc_code: ''
  });
  const [bankError, setBankError] = useState<string>('');

  const fetchDepartmentDropdown = async () => {
    try {
      const response = await departmentsAPI.dropDown();
      if (response.success) {
        // setDepartmentDropdown(response.data);
      } else {
        errorModal(response.message);
      }
    } catch (err: any) {
      console.error('Failed to fetch department dropdown:', err);
    }
  };

  const fetchFinancialPowersDropdown = async () => {
    if (uploadForm.approval_type === 'Invoice Payment - Financial approval available') {
      setLoadingFinancialPowers(true);
      try {
        const response = await financialPowersAPI.dropdown();
        if (response.success) {
          setFinancialPowersDropdown(response.data || []);
        } else {
          errorModal(response.message || 'Failed to fetch financial powers');
          setFinancialPowersDropdown([]);
        }
      } catch (err: any) {
        console.error('Failed to fetch financial powers dropdown:', err);
        errorModal('Failed to fetch financial powers');
        setFinancialPowersDropdown([]);
      } finally {
        setLoadingFinancialPowers(false);
      }
    } else {
      setFinancialPowersDropdown([]);
      setUploadForm(prev => ({ ...prev, proposal: '' }));
    }
  };

  useEffect(() => {
    fetchDepartmentDropdown();
  }, []);

  useEffect(() => {
    fetchFinancialPowersDropdown();
  }, [uploadForm.approval_type]);


  // Reset to first page when search term changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  // Reset to first page when status filter changes
  useEffect(() => {
    setPage(1);
  }, [statusFilter]);

  // Handle clicks outside dropdowns to close them
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (departmentDropdownRef.current && !departmentDropdownRef.current.contains(event.target as Node)) {
        //   setDepartmentDropdownOpen(false);
        //   setDepartmentSearchTerm('');
      }
      if (approvalTypeDropdownRef.current && !approvalTypeDropdownRef.current.contains(event.target as Node)) {
        setApprovalTypeDropdownOpen(false);
      }
      if (proposalDropdownRef.current && !proposalDropdownRef.current.contains(event.target as Node)) {
        setProposalDropdownOpen(false);
        setProposalSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);



  const getStatusBadge = (status: string) => {
    // Check if status contains "rejected" (case-insensitive)
    const isRejected = status && status.toLowerCase().includes('rejected');

    // If rejected, show in red
    if (isRejected) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        <X className="w-3 h-3 mr-1" />
        {status}
      </span>;
    }

    // Check if status contains "approved" (case-insensitive)
    const isApproved = status && status.toLowerCase().includes('approved');

    // If approved, show in green
    if (isApproved) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <Check className="w-3 h-3 mr-1" />
        {status}
      </span>;
    }

    // Check if status contains "pending" (case-insensitive)
    const isPending = status && status.toLowerCase().includes('pending');

    // If pending, show in yellow
    if (isPending) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        <Clock className="w-3 h-3 mr-1" />
        {status}
      </span>;
    }

    switch (status) {
      case 'paid':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <Check className="w-3 h-3 mr-1" />
          Paid
        </span>;
      case 'approved':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
          <Check className="w-3 h-3 mr-1" />
          Approved
        </span>;
      case 'pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          <Clock className="w-3 h-3 mr-1" />
          Pending
        </span>;
      case 'overdue':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <AlertCircle className="w-3 h-3 mr-1" />
          Overdue
        </span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  const filteredInvoices = invoices || [];

  // Use server-provided stats for totals


  const [errors, setErrors] = useState<ValidationErrors>({});

  // Fetch banks on mount
  const fetchBanks = async (vendor_id: number) => {
    try {
      const response = await bankAPI.getAll(vendor_id);
      if (response.success && response.data) {
        setBankOptions(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch banks', err);
    }
  };

  const validateForm = (): ValidationErrors => {
    const newErrors: ValidationErrors = {};

    // Department validation
    // if (!uploadForm.department_id) {
    //   newErrors.department_id = 'Department is required';
    // }

    // Vendor ID validation
    // if (!uploadForm.vendor_id) {
    //   newErrors.vendor_id = 'Vendor is required';
    // }

    // Invoice Number validation
    // if (!uploadForm.invoice_number.trim()) {
    //   newErrors.invoice_number = "Invoice Number is required.";
    // } else if (uploadForm.invoice_number.length > 50) {
    //   newErrors.invoice_number = "Invoice Number must be 50 characters or less.";
    // } else if (!/^[A-Za-z0-9][A-Za-z0-9\-\/._]{2,19}$/.test(uploadForm.invoice_number)) {
    //   newErrors.invoice_number =
    //     "Invoice Number must be 3–20 characters (letters, numbers, -, /, ., _).";
    // }

    // Invoice Date validation
    // if (!uploadForm.invoice_date) {
    //   newErrors.invoice_date = 'Invoice date is required';
    // } else {
    //   const invoiceDate = new Date(uploadForm.invoice_date);
    //   const today = new Date();
    //   today.setHours(23, 59, 59, 999); // Allow same-day invoices
    //   if (isNaN(invoiceDate.getTime())) {
    //     newErrors.invoice_date = 'Invalid invoice date';
    //   } else if (invoiceDate > today) {
    //     newErrors.invoice_date = 'Invoice date cannot be in the future';
    //   }
    // }

    // Due Date validation
    // if (!uploadForm.due_date) {
    //   newErrors.due_date = 'Due date is required';
    // } else {
    //   const invoiceDate = new Date(uploadForm.invoice_date);
    //   const dueDate = new Date(uploadForm.due_date);
    //   if (isNaN(dueDate.getTime())) {
    //     newErrors.due_date = 'Invalid due date';
    //   } else if (dueDate < invoiceDate) {
    //     newErrors.due_date = 'Due date cannot be before invoice date';
    //   }
    // }

    // Total Amount validation
    // if (!uploadForm.total_amount) {
    //   newErrors.total_amount = 'Total amount is required';
    // } else {
    //   const totalAmount = Number(uploadForm.total_amount);
    //   if (isNaN(totalAmount) || totalAmount <= 0) {
    //     newErrors.total_amount = 'Total amount must be a positive number';
    //   } else if (!/^\d+(\.\d{1,2})?$/.test(uploadForm.total_amount)) {
    //     newErrors.total_amount = 'Total amount must have at most 2 decimal places';
    //   }
    // }

    // Subtotal validation (required)
    // if (!uploadForm.subtotal) {
    //   newErrors.subtotal = 'Subtotal is required';
    // } else {
    //   const subtotal = Number(uploadForm.subtotal);
    //   if (isNaN(subtotal) || subtotal < 0) {
    //     newErrors.subtotal = 'Subtotal must be a non-negative number';
    //   } else if (!/^\d+(\.\d{1,2})?$/.test(uploadForm.subtotal)) {
    //     newErrors.subtotal = 'Subtotal must have at most 2 decimal places';
    //   }
    // }

    // Tax Amount validation (required)
    // if (!uploadForm.tax_amount) {
    //   newErrors.tax_amount = 'Tax amount is required';
    // } else {
    //   const taxAmount = Number(uploadForm.tax_amount);
    //   if (isNaN(taxAmount) || taxAmount < 0) {
    //     newErrors.tax_amount = 'Tax amount must be a non-negative number';
    //   } else if (!/^\d+(\.\d{1,2})?$/.test(uploadForm.tax_amount)) {
    //     newErrors.tax_amount = 'Tax amount must have at most 2 decimal places';
    //   }
    // }

    // PO Number validation (optional)
    // if (uploadForm.po_number) {
    //   if (uploadForm.po_number.length > 50) {
    //     newErrors.po_number = 'PO number must be 50 characters or less';
    //   } else if (!/^[A-Za-z0-9-_]+$/.test(uploadForm.po_number)) {
    //     newErrors.po_number = 'PO number can only contain letters, numbers, hyphens, and underscores';
    //   }
    // }

    // Description validation (optional)
    // if (uploadForm.description && uploadForm.description.length > 500) {
    //   newErrors.description = 'Description must be 500 characters or less';
    // }

    // Approval type validation
    if (!uploadForm.approval_type) {
      newErrors.approval_type = 'Approval type is required';
    }

    // Proposal validation - required only if "Invoice Payment - Financial approval available" is selected
    if (uploadForm.approval_type === 'Invoice Payment - Financial approval available') {
      if (!uploadForm.proposal) {
        newErrors.proposal = 'Proposal is required';
      }
    }

    // File validation
    if (!uploadForm.file) {
      newErrors.file = 'Invoice file is required';
    } else {
      const maxFileSize = 10 * 1024 * 1024; // 10 MB
      const allowedTypes = ['application/pdf'];
      if (!allowedTypes.includes(uploadForm.file.type)) {
        newErrors.file = 'File must be in PDF format';
      } else if (uploadForm.file.size > maxFileSize) {
        newErrors.file = 'File size exceeds 10MB limit';
      }
    }

    return newErrors;
  };


  const handleUploadSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    const validationErrors = validateForm();
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      // alert('Please correct the errors in the form');
      return;
    }

    // Check for validation messages from API checks
    // if (validationMessages.invoice_number || validationMessages.registration_number || validationMessages.due_date) {
    //   if (validationMessages.invoice_number) {
    //     errorModal(validationMessages.invoice_number);
    //   }
    //   if (validationMessages.registration_number) {
    //     errorModal(validationMessages.registration_number);
    //   }
    //   if (validationMessages.due_date) {
    //     errorModal(validationMessages.due_date);
    //   }
    //   return;
    // }

    setIsSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('department_id', user?.department_id?.toString() || '');
      formData.append('approval_type', uploadForm.approval_type || '');
      if (uploadForm.approval_type === 'Invoice Payment - Financial approval available' && uploadForm.proposal) {
        formData.append('proposal', uploadForm.proposal);
      }
      // formData.append('vendor_id', uploadForm.vendor_id);
      // formData.append('invoice_number', uploadForm.invoice_number);
      // formData.append('invoice_date', uploadForm.invoice_date);
      // formData.append('due_date', uploadForm.due_date);
      // formData.append('total_amount', uploadForm.total_amount);
      // formData.append('subtotal', uploadForm.subtotal || '');
      // formData.append('tax_amount', uploadForm.tax_amount || '');
      // formData.append('description', uploadForm.description || '');
      // formData.append('notes', uploadForm.notes || '');
      // formData.append('po_number', uploadForm.po_number || '');
      // if (uploadForm.registration_number && uploadForm.registration_number.trim()) {
      //   formData.append('registration_number', uploadForm.registration_number.trim());
      // }
      formData.append('use_ocr', uploadForm.use_ocr.toString());
      if (uploadForm.file) {
        formData.append('invoice_file', uploadForm.file);
      }

      const response = await vendorInvoicesAPI.upload(formData);
      if (response.success) {
        toast.success('Invoice uploaded successfully');

        // After upload, fetch the invoices list and open the latest one
        try {
          const listRes = await vendorInvoicesAPI.getAll();
          if (listRes.success && Array.isArray(listRes.data) && listRes.data.length > 0) {
            const latest = [...listRes.data].sort((a, b) => {
              const aTime = Date.parse((a as any).created_at || '');
              const bTime = Date.parse((b as any).created_at || '');
              if (!Number.isNaN(aTime) && !Number.isNaN(bTime)) {
                return bTime - aTime;
              }
              return (b.id || 0) - (a.id || 0);
            })[0];

            if (latest) {
              setSelectedInvoice(latest as VendorInvoice);
              setShowDetailModal(true);
              try {
                const auditRes = await vendorInvoicesAPI.getAuditTrail((latest as VendorInvoice).id);
                if (auditRes.success) {
                  setAuditTrailData(auditRes.data);
                }
              } catch (auditError) {
                console.error('Error fetching status trail:', auditError);
              }
            }
          }
        } catch (fetchError) {
          console.error('Error fetching latest invoices:', fetchError);
        }
      } else {
        errorModal(response.error || 'Error uploading invoice. Please try again.');
      }

      setShowUploadModal(false);
      setUploadForm({
        department_id: '',
        vendor_id: '',
        invoice_number: '',
        invoice_date: new Date().toISOString().split('T')[0],
        due_date: '',
        total_amount: '',
        subtotal: '',
        tax_amount: '',
        description: '',
        notes: '',
        po_number: '',
        registration_number: '',
        approval_type: '',
        proposal: '',
        use_ocr: false,
        approver_1: '',
        approver_2: '',
        file: null
      });
      setErrors({});
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      refetch();
    } catch (error) {
      console.error('Error uploading invoice:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowUploadModal(false);
    setErrors({});
    setUploadForm({
      department_id: '',
      vendor_id: '',
      invoice_number: '',
      invoice_date: new Date().toISOString().split('T')[0],
      due_date: '',
      total_amount: '',
      subtotal: '',
      tax_amount: '',
      description: '',
      notes: '',
      po_number: '',
      registration_number: '',
      approver_1: '',
      approver_2: '',
      approval_type: '',
      proposal: '',
      use_ocr: false,
      file: null
    });
    // Reset dropdown states
    // setDepartmentDropdownOpen(false);
    // setDepartmentSearchTerm('');
    setApprovalTypeDropdownOpen(false);
    setProposalDropdownOpen(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Debounced validation for invoice number duplicate check
  // useEffect(() => {
  //   if (!uploadForm.vendor_id || !uploadForm.invoice_number.trim()) {
  //     setValidationMessages(prev => ({ ...prev, invoice_number: undefined }));
  //     return;
  //   }

  //   const timeoutId = setTimeout(async () => {
  //     try {
  //       const response = await vendorInvoicesAPI.checkDuplicateInvoice(
  //         Number(uploadForm.vendor_id),
  //         uploadForm.invoice_number.trim()
  //       );

  //       if (response.success && response.message === 'Invoice number already exists') {
  //         setValidationMessages(prev => ({ ...prev, invoice_number: 'This invoice number already exists for this vendor' }));
  //       } else {
  //         setValidationMessages(prev => ({ ...prev, invoice_number: undefined }));
  //       }
  //     } catch (error) {
  //       console.error('Error checking duplicate invoice:', error);
  //     }
  //   }, 500); // 500ms debounce

  //   return () => clearTimeout(timeoutId);
  // }, [uploadForm.vendor_id, uploadForm.invoice_number]);

  // Debounced validation for registration number check
  // useEffect(() => {
  //   // Clear validation if no vendor selected or registration number is empty/null/undefined
  //   if (!uploadForm.vendor_id || !uploadForm.registration_number || !uploadForm.registration_number.trim()) {
  //     setValidationMessages(prev => ({ ...prev, registration_number: undefined }));
  //     return;
  //   }

  //   const timeoutId = setTimeout(async () => {
  //     try {
  //       const response = await vendorInvoicesAPI.checkRegistrationNumber(
  //         Number(uploadForm.vendor_id),
  //         uploadForm.registration_number.trim()
  //       );
  //       if (response.success && response.message === 'Registration number is invalid pls check') {
  //         setValidationMessages(prev => ({ ...prev, registration_number: 'Registration number does not match the vendor\'s registered number' }));
  //       } else if (response.success && response.message === 'Registration number is valid') {
  //         setValidationMessages(prev => ({ ...prev, registration_number: undefined }));
  //       } else {
  //         // If response doesn't indicate validation error, clear any previous error
  //         setValidationMessages(prev => ({ ...prev, registration_number: undefined }));
  //       }
  //     } catch (error) {
  //       // On error, don't show validation error - allow empty registration numbers
  //       console.error('Error checking registration number:', error);
  //       setValidationMessages(prev => ({ ...prev, registration_number: undefined }));
  //     }
  //   }, 500); // 500ms debounce

  //   return () => clearTimeout(timeoutId);
  // }, [uploadForm.vendor_id, uploadForm.registration_number]);


  const handleStatusUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedInvoice) return;

    setIsSubmitting(true);
    try {
      // If approving and user is invoice_approver, ensure bank is selected or created
      let bankIdToUse: string | undefined = undefined;
      if (statusForm.status === 'approved' && userPermissions === 'invoice_approver') {

        if (showAddBank) {
          try {
            const createResp = await bankAPI.create({
              bank_name: addBankForm.bank_name,
              customer_name: addBankForm.account_holder_name,
              account_number: addBankForm.account_number,
              account_type: addBankForm.account_type,
              ifsc_code: addBankForm.ifsc_code,
              vendor_id: selectedInvoice.vendor_id
            });
            if (createResp.success) {
              bankIdToUse = String(createResp.data.bankId);
              // refresh bank list to include the new bank
              try {
                fetchBanks(selectedInvoice.vendor_id);
              } catch { }
            } else {
              setBankError(createResp.error || 'Failed to add bank.');
              setIsSubmitting(false);
              return;
            }
          } catch (err: any) {
            setBankError(err?.response?.data?.error || err?.message || 'Failed to add bank.');
            setIsSubmitting(false);
            return;
          }
        } else {
          if (!selectedBankId) {
            setBankError('Please select a bank or add a new one.');
            setIsSubmitting(false);
            return;
          }
          bankIdToUse = selectedBankId;
        }

        // If needed, pass bankIdToUse to status update API when backend supports it.
        // Current API signature does not accept bank_id; proceed with existing payload.
      }

      const response = await vendorInvoicesAPI.updateStatus(selectedInvoice.id, statusForm.status, statusForm.notes, bankIdToUse);
      if (response.success) {
        toast.success('Status updated successfully');
      } else {
        errorModal(response.error || 'Error updating status. Please try again.');
      }
      setShowStatusModal(false);
      setSelectedInvoice(null);
      setStatusForm({ status: '', notes: '' });
      setSelectedBankId('');
      setShowAddBank(false);
      setAddBankForm({ bank_name: '', account_holder_name: '', account_number: '', account_type: '', ifsc_code: '' });
      setBankError('');
      refetch();
    } catch (error) {
      console.error('Error updating status:', error);
      errorModal('Error updating status. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // original handleMarkPaid
  const handleMarkPaid = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedInvoice) return;

    const errors: { reference_number?: string } = {};

    if (!paymentForm.reference_number.trim()) {
      errors.reference_number = 'Transaction ID is required.';
    }

    if (Object.keys(errors).length > 0) {
      setPaymentErrors(errors);
      return;
    }

    setPaymentErrors({});
    setIsSubmitting(true);
    try {

      const existingPaid = parseFloat(selectedInvoice.paid_amount?.toString() || "0");
      const newPaid = existingPaid + parseFloat(paymentForm.paid_amount);

      if (paymentForm.broucher) {
        await vendorInvoicesAPI.markPaidWithAttachment(selectedInvoice.id, {
          paid_amount: newPaid,
          payment_date: paymentForm.payment_date,
          notes: paymentForm.notes,
          reference_number: paymentForm.reference_number,
          payment_attachement: paymentForm.broucher,
        });
      } else {
        await vendorInvoicesAPI.markPaid(
          selectedInvoice.id,
          newPaid,
          paymentForm.payment_date,
          paymentForm.notes,
          paymentForm.reference_number
        );
      }

      setShowPaymentModal(false);
      setSelectedInvoice(null);
      setPaymentForm({
        paid_amount: "",
        payment_date: new Date().toISOString().split("T")[0],
        notes: "",
        reference_number: "",
        broucher: null
      });
      setPaymentErrors({});
      refetch();
    } catch (error) {
      console.error("Error marking as paid:", error);
      errorModal("Error recording payment. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    setPaymentErrors({});
  };

  const handleViewAttachment = async (url: string, filename: string, isMain: boolean = false, invoiceId?: number) => {
    setLoadingAttachment(true);
    setCurrentAttachmentName(filename);

    try {
      // Always download via API first (if we have invoiceId) to ensure proper auth
      // This avoids CORS issues and works with blob URLs in iframe
      if (invoiceId) {
        try {
          let response;
          if (isMain) {
            // Download main attachment
            response = await vendorInvoicesAPI.download(invoiceId);
          } else {
            // Download additional attachment - need filename
            response = await vendorInvoicesAPI.downloadAdditionalAttachment(invoiceId, filename);
          }

          const contentType = response.headers?.['content-type'] || response.headers?.['Content-Type'] || 'application/pdf';
          const blob = new Blob([response.data], { type: contentType });
          const blobUrl = window.URL.createObjectURL(blob);

          // Remove PDF toolbar by appending #toolbar=0
          const urlWithToolbarRemoved = contentType.includes('pdf')
            ? `${blobUrl}#toolbar=0`
            : blobUrl;

          setCurrentAttachmentUrl(urlWithToolbarRemoved);
          setOriginalAttachmentUrl(url || ''); // Store original URL for fallback
          setShowAttachmentViewer(true);
        } catch (apiError: any) {
          console.error('API download failed, trying direct URL fetch:', apiError);
          // If API download fails, try fetching the URL directly
          if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
            try {
              const fetchResponse = await fetch(url, {
                method: 'GET',
                mode: 'cors',
                credentials: 'include',
                headers: {
                  'Accept': 'application/pdf,image/*,*/*'
                }
              });

              if (fetchResponse.ok) {
                const blob = await fetchResponse.blob();
                const blobUrl = window.URL.createObjectURL(blob);

                // Remove PDF toolbar by appending #toolbar=0
                const contentType = fetchResponse.headers.get('content-type') || '';
                const urlWithToolbarRemoved = contentType.includes('pdf')
                  ? `${blobUrl}#toolbar=0`
                  : blobUrl;

                setCurrentAttachmentUrl(urlWithToolbarRemoved);
                setOriginalAttachmentUrl(url); // Store original URL for fallback
                setShowAttachmentViewer(true);
              } else {
                throw new Error(`Fetch failed: ${fetchResponse.status}`);
              }
            } catch (fetchError: any) {
              console.error('Both API and fetch failed, opening in new tab:', fetchError);
              // If both fail, open in new tab as fallback
              if (url) {
                window.open(url, '_blank');
                toast.success('Opening attachment in new tab');
                setLoadingAttachment(false);
                return;
              } else {
                throw fetchError;
              }
            }
          } else {
            throw apiError;
          }
        }
      } else if (url && (url.startsWith('http://') || url.startsWith('https://'))) {
        // For direct URLs when no invoiceId, fetch and convert to blob URL
        try {
          const response = await fetch(url, {
            method: 'GET',
            mode: 'cors',
            credentials: 'include',
            headers: {
              'Accept': 'application/pdf,image/*,*/*'
            }
          });

          if (response.ok) {
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);

            // Remove PDF toolbar by appending #toolbar=0
            const contentType = response.headers.get('content-type') || '';
            const urlWithToolbarRemoved = contentType.includes('pdf') || filename.toLowerCase().endsWith('.pdf')
              ? `${blobUrl}#toolbar=0`
              : blobUrl;

            setCurrentAttachmentUrl(urlWithToolbarRemoved);
            setOriginalAttachmentUrl(url); // Store original URL for fallback
            setShowAttachmentViewer(true);
          } else {
            throw new Error(`Failed to fetch: ${response.status} ${response.statusText}`);
          }
        } catch (fetchError: any) {
          console.error('Fetch failed, opening in new tab:', fetchError);
          // If fetch fails, open in new tab as fallback
          window.open(url, '_blank');
          toast.success('Opening attachment in new tab');
          setLoadingAttachment(false);
          return;
        }
      } else {
        throw new Error('No valid URL or invoice selected');
      }
    } catch (error: any) {
      console.error('Error viewing attachment:', error);
      errorModal('Failed to load attachment. Please try again.');
    } finally {
      setLoadingAttachment(false);
    }
  };

  const closeAttachmentViewer = () => {
    // Revoke blob URL if it was created
    if (currentAttachmentUrl && currentAttachmentUrl.startsWith('blob:')) {
      window.URL.revokeObjectURL(currentAttachmentUrl);
    }
    setShowAttachmentViewer(false);
    setCurrentAttachmentUrl('');
    setCurrentAttachmentName('');
    setOriginalAttachmentUrl('');
  };

  const handleViewMainAttachment = async (invoice: VendorInvoice) => {
    await handleViewAttachment(
      invoice.attachment_url || '',
      invoice.attachment_filename || 'Main Attachment',
      true,
      invoice.id
    );
  };

  // Handle payment attachment viewing
  const handleDownloadAttachment = (filepathOrUrl: string, filename?: string) => {
    if (!filepathOrUrl) return;

    // Check if it's already a full URL
    if (filepathOrUrl.startsWith('http://') || filepathOrUrl.startsWith('https://')) {
      handleViewAttachment(
        filepathOrUrl,
        filename || 'Payment Attachment',
        false,
        selectedInvoice?.id
      );
      return;
    }

    // Otherwise, construct the URL
    const isAbsolute = /^https?:\/\//i.test(filepathOrUrl);
    const base = (import.meta as any).env?.VITE_API_BASE_URL || '/api';
    const url = isAbsolute ? filepathOrUrl : `${base}${filepathOrUrl.startsWith('/') ? filepathOrUrl : `/${filepathOrUrl}`}`;
    handleViewAttachment(
      url,
      filename || 'Payment Attachment',
      false,
      selectedInvoice?.id
    );
  };

  const handleDelete = async (invoice: VendorInvoice) => {
    confirmToast(`Are you sure you want to delete invoice ${invoice.invoice_number}?`, async () => {
      try {
        const response = await vendorInvoicesAPI.delete(invoice.id);
        if (response.success) {
          toast.success('Invoice deleted successfully');
        } else {
          errorModal(response.error || 'Error deleting invoice. Please try again.');
        }
        refetch();
      } catch (error) {
        console.error('Error deleting invoice:', error);
        errorModal('Error deleting invoice. Please try again.');
      }
    });
  };

  // const handleDownloadPaymentDetails = async (invoice: VendorInvoice) => {
  //   try {
  //     // Fetch full invoice details to get credit_accounts and nastro_accounts
  //     const invoiceResponse = await vendorInvoicesAPI.getById(invoice.id);
  //     if (!invoiceResponse.success) {
  //       errorModal('Failed to fetch invoice details');
  //       return;
  //     }

  //     const fullInvoice = invoiceResponse.data as any;
  //     const currency = invoice.currency || 'GBP';
  //     // const currencySymbol = currency === 'GBP' ? '£' : currency === 'USD' ? '$' : currency === 'EUR' ? '€' : currency === 'INR' ? '₹' : currency;
  //     const currencySymbol = currency;

  //     // Initialize PDF
  //     const pdf = new jsPDF();
  //     const pageWidth = pdf.internal.pageSize.getWidth();
  //     const pageHeight = pdf.internal.pageSize.getHeight();
  //     const margin = 20;
  //     let yPosition = margin;

  //     // Helper function to add a new page if needed
  //     const checkNewPage = (requiredSpace: number) => {
  //       if (yPosition + requiredSpace > pageHeight - margin) {
  //         pdf.addPage();
  //         yPosition = margin;
  //       }
  //     };

  //     // Title
  //     pdf.setFontSize(16);
  //     pdf.setFont('helvetica', 'bold');
  //     pdf.text('Payment Details', pageWidth / 2, yPosition, { align: 'center' });
  //     yPosition += 10;

  //     // Invoice Information
  //     pdf.setFontSize(10);
  //     pdf.setFont('helvetica', 'normal');
  //     pdf.text(`Invoice Number: ${invoice.invoice_number}`, margin, yPosition);
  //     yPosition += 5;
  //     pdf.text(`Invoice Date: ${new Date(invoice.invoice_date).toLocaleDateString()}`, margin, yPosition);
  //     yPosition += 5;
  //     pdf.text(`Vendor: ${invoice.vendor_name || 'N/A'}`, margin, yPosition);
  //     yPosition += 10;

  //     // DEBITS Section
  //     const debitAccounts = fullInvoice.finance_bank_details || [];
  //     const allDebitAccounts = [...debitAccounts];

  //     if (allDebitAccounts.length > 0) {
  //       checkNewPage(40);
  //       pdf.setFontSize(12);
  //       pdf.setFont('helvetica', 'bold');
  //       pdf.text('DEBITS', margin, yPosition);
  //       yPosition += 10;

  //       // Table setup - vertical format with labels in first column
  //       const tableStartX = margin;
  //       const tableEndX = pageWidth - margin;
  //       const rowHeight = 7;

  //       // Column widths - 3 columns: Label | Value | Amount
  //       const labelColWidth = 60; // Labels column (A/c No, Account Name, remark)
  //       const amountColWidth = 50; // Amount column
  //       const valueColWidth = tableEndX - tableStartX - labelColWidth - amountColWidth; // Value column (flexible)

  //       const labelColEnd = tableStartX + labelColWidth;
  //       const valueColEnd = labelColEnd + valueColWidth;
  //       const amountColEnd = tableEndX;

  //       pdf.setFontSize(9);
  //       let totalDebitAmount = 0;

  //       // Draw table for each account
  //       allDebitAccounts.forEach((account: any) => {
  //         const accountNumber = account.account_number || 'N/A';
  //         const accountName = account.account_name || 'N/A';
  //         const narration = account.narration || '';
  //         const amount = parseFloat(invoice.total_amount?.toString() || '0');

  //         totalDebitAmount += amount;

  //         // Check if we need a new page before starting this account's table
  //         checkNewPage(25);

  //         const accountTableStartY = yPosition;
  //         let currentY = yPosition;

  //         // Draw top border
  //         pdf.setLineWidth(0.5);
  //         pdf.line(tableStartX, currentY, tableEndX, currentY);
  //         currentY += rowHeight;

  //         // Row 1: A/c No
  //         pdf.setLineWidth(0.2);
  //         pdf.line(tableStartX, currentY, tableEndX, currentY);
  //         pdf.line(labelColEnd, accountTableStartY, labelColEnd, currentY);
  //         pdf.line(valueColEnd, accountTableStartY, valueColEnd, currentY);
  //         pdf.line(amountColEnd, accountTableStartY, amountColEnd, currentY);

  //         pdf.setFont('helvetica', 'bold');
  //         pdf.text('A/c No', tableStartX + 2, currentY - 3);
  //         pdf.setFont('helvetica', 'normal');
  //         pdf.text(accountNumber, labelColEnd + 2, currentY - 3);
  //         pdf.setFont('helvetica', 'bold');
  //         pdf.text('Amount', valueColEnd + 2, currentY - 3);
  //         currentY += rowHeight;

  //         // Row 2: Account Name
  //         pdf.line(tableStartX, currentY, tableEndX, currentY);
  //         pdf.line(labelColEnd, currentY - rowHeight, labelColEnd, currentY);
  //         pdf.line(valueColEnd, currentY - rowHeight, valueColEnd, currentY);
  //         pdf.line(amountColEnd, currentY - rowHeight, amountColEnd, currentY);

  //         pdf.setFont('helvetica', 'bold');
  //         pdf.text('Account Name', tableStartX + 2, currentY - 3);
  //         pdf.setFont('helvetica', 'normal');
  //         pdf.text(accountName, labelColEnd + 2, currentY - 3);
  //         pdf.text(`${currencySymbol}${amount.toFixed(2)}`, amountColEnd - 2, currentY - 3, { align: 'right' });
  //         currentY += rowHeight;

  //         // Row 3: remark
  //         pdf.line(tableStartX, currentY, tableEndX, currentY);
  //         pdf.line(labelColEnd, currentY - rowHeight, labelColEnd, currentY);
  //         pdf.line(valueColEnd, currentY - rowHeight, valueColEnd, currentY);
  //         pdf.line(amountColEnd, currentY - rowHeight, amountColEnd, currentY);

  //         pdf.setFont('helvetica', 'bold');
  //         pdf.text('remark', tableStartX + 2, currentY - 3);
  //         pdf.setFont('helvetica', 'normal');
  //         pdf.text(narration || '', labelColEnd + 2, currentY - 3);
  //         currentY += rowHeight;

  //         // Draw bottom border
  //         pdf.setLineWidth(0.5);
  //         pdf.line(tableStartX, currentY, tableEndX, currentY);
  //         pdf.line(labelColEnd, accountTableStartY, labelColEnd, currentY);
  //         pdf.line(valueColEnd, accountTableStartY, valueColEnd, currentY);
  //         pdf.line(amountColEnd, accountTableStartY, amountColEnd, currentY);
  //         // Draw left border (complete from top to bottom)
  //         pdf.line(tableStartX, accountTableStartY, tableStartX, currentY);

  //         yPosition = currentY + 5; // Add spacing between account tables
  //       });

  //       // Total row
  //       yPosition += 3;
  //       checkNewPage(12);
  //       pdf.setLineWidth(0.5);
  //       pdf.line(tableStartX, yPosition, tableEndX, yPosition);
  //       yPosition += 8;
  //       pdf.setFont('helvetica', 'bold');
  //       pdf.text('TOTAL', tableStartX, yPosition);
  //       pdf.text(`${currencySymbol}${totalDebitAmount.toFixed(2)}`, amountColEnd - 2, yPosition, { align: 'right' });
  //       yPosition += 15;
  //     }

  //     // CREDITS Section
  //     const creditAccounts = fullInvoice.credit_accounts || [];

  //     if (creditAccounts.length > 0) {
  //       checkNewPage(40);
  //       pdf.setFontSize(12);
  //       pdf.setFont('helvetica', 'bold');
  //       pdf.text('CREDITS', margin, yPosition);
  //       yPosition += 10;

  //       // Table setup - vertical format with labels in first column
  //       const tableStartX = margin;
  //       const tableEndX = pageWidth - margin;
  //       const rowHeight = 7;

  //       // Column widths - 3 columns: Label | Value | Amount
  //       const labelColWidth = 60; // Labels column
  //       const amountColWidth = 50; // Amount column
  //       const valueColWidth = tableEndX - tableStartX - labelColWidth - amountColWidth; // Value column (flexible)

  //       const labelColEnd = tableStartX + labelColWidth;
  //       const valueColEnd = labelColEnd + valueColWidth;
  //       const amountColEnd = tableEndX;

  //       pdf.setFontSize(9);
  //       let totalCreditAmount = 0;

  //       // Draw table for each account
  //       creditAccounts.forEach((account: any) => {
  //         const bankName = account.bank_name || 'N/A';
  //         const beneficiaryName = account.beneficiary_name || 'N/A';
  //         const accountNumber = account.account_no || 'N/A';
  //         const sortCode = account.sort_code || '';
  //         const ibanNo = account.iban_no || '';
  //         const bic = account.bic || '';
  //         const narration = account.narration || '';
  //         const amount = parseFloat(invoice.total_amount?.toString() || '0');

  //         totalCreditAmount += amount;

  //         // Check if we need a new page before starting this account's table
  //         // Calculate number of rows needed
  //         let rowCount = 3; // Bank Name, Beneficiary Name, Account No (always shown)
  //         if (sortCode) rowCount++;
  //         if (ibanNo) rowCount++;
  //         if (bic) rowCount++;
  //         if (narration) rowCount++;
  //         rowCount++; // remark row (always shown)

  //         checkNewPage(rowCount * rowHeight + 10);

  //         const accountTableStartY = yPosition;
  //         let currentY = yPosition;

  //         // Draw top border
  //         pdf.setLineWidth(0.5);
  //         pdf.line(tableStartX, currentY, tableEndX, currentY);
  //         currentY += rowHeight;

  //         // Row 1: Bank Name
  //         pdf.setLineWidth(0.2);
  //         pdf.line(tableStartX, currentY, tableEndX, currentY);
  //         pdf.line(labelColEnd, accountTableStartY, labelColEnd, currentY);
  //         pdf.line(valueColEnd, accountTableStartY, valueColEnd, currentY);
  //         pdf.line(amountColEnd, accountTableStartY, amountColEnd, currentY);

  //         pdf.setFont('helvetica', 'bold');
  //         pdf.text('Bank Name', tableStartX + 2, currentY - 3);
  //         pdf.setFont('helvetica', 'normal');
  //         pdf.text(bankName, labelColEnd + 2, currentY - 3);
  //         pdf.setFont('helvetica', 'bold');
  //         pdf.text('Amount', valueColEnd + 2, currentY - 3);
  //         currentY += rowHeight;

  //         // Row 2: Beneficiary Name
  //         pdf.line(tableStartX, currentY, tableEndX, currentY);
  //         pdf.line(labelColEnd, currentY - rowHeight, labelColEnd, currentY);
  //         pdf.line(valueColEnd, currentY - rowHeight, valueColEnd, currentY);
  //         pdf.line(amountColEnd, currentY - rowHeight, amountColEnd, currentY);

  //         pdf.setFont('helvetica', 'bold');
  //         pdf.text('Beneficiary Name', tableStartX + 2, currentY - 3);
  //         pdf.setFont('helvetica', 'normal');
  //         pdf.text(beneficiaryName, labelColEnd + 2, currentY - 3);
  //         pdf.text(`${currencySymbol}${amount.toFixed(2)}`, amountColEnd - 2, currentY - 3, { align: 'right' });
  //         currentY += rowHeight;

  //         // Row 3: Account No
  //         pdf.line(tableStartX, currentY, tableEndX, currentY);
  //         pdf.line(labelColEnd, currentY - rowHeight, labelColEnd, currentY);
  //         pdf.line(valueColEnd, currentY - rowHeight, valueColEnd, currentY);
  //         pdf.line(amountColEnd, currentY - rowHeight, amountColEnd, currentY);

  //         pdf.setFont('helvetica', 'bold');
  //         pdf.text('Account No', tableStartX + 2, currentY - 3);
  //         pdf.setFont('helvetica', 'normal');
  //         pdf.text(accountNumber, labelColEnd + 2, currentY - 3);
  //         currentY += rowHeight;

  //         // Row 4: Sort Code (if available)
  //         if (sortCode) {
  //           pdf.line(tableStartX, currentY, tableEndX, currentY);
  //           pdf.line(labelColEnd, currentY - rowHeight, labelColEnd, currentY);
  //           pdf.line(valueColEnd, currentY - rowHeight, valueColEnd, currentY);
  //           pdf.line(amountColEnd, currentY - rowHeight, amountColEnd, currentY);

  //           pdf.setFont('helvetica', 'bold');
  //           pdf.text('Sort Code', tableStartX + 2, currentY - 3);
  //           pdf.setFont('helvetica', 'normal');
  //           pdf.text(sortCode, labelColEnd + 2, currentY - 3);
  //           currentY += rowHeight;
  //         }

  //         // Row 5: IBAN No (if available)
  //         if (ibanNo) {
  //           pdf.line(tableStartX, currentY, tableEndX, currentY);
  //           pdf.line(labelColEnd, currentY - rowHeight, labelColEnd, currentY);
  //           pdf.line(valueColEnd, currentY - rowHeight, valueColEnd, currentY);
  //           pdf.line(amountColEnd, currentY - rowHeight, amountColEnd, currentY);

  //           pdf.setFont('helvetica', 'bold');
  //           pdf.text('IBAN No', tableStartX + 2, currentY - 3);
  //           pdf.setFont('helvetica', 'normal');
  //           pdf.text(ibanNo, labelColEnd + 2, currentY - 3);
  //           currentY += rowHeight;
  //         }

  //         // Row 6: BIC (if available)
  //         if (bic) {
  //           pdf.line(tableStartX, currentY, tableEndX, currentY);
  //           pdf.line(labelColEnd, currentY - rowHeight, labelColEnd, currentY);
  //           pdf.line(valueColEnd, currentY - rowHeight, valueColEnd, currentY);
  //           pdf.line(amountColEnd, currentY - rowHeight, amountColEnd, currentY);

  //           pdf.setFont('helvetica', 'bold');
  //           pdf.text('BIC', tableStartX + 2, currentY - 3);
  //           pdf.setFont('helvetica', 'normal');
  //           pdf.text(bic, labelColEnd + 2, currentY - 3);
  //           currentY += rowHeight;
  //         }

  //         // Last row: remark
  //         pdf.line(tableStartX, currentY, tableEndX, currentY);
  //         pdf.line(labelColEnd, currentY - rowHeight, labelColEnd, currentY);
  //         pdf.line(valueColEnd, currentY - rowHeight, valueColEnd, currentY);
  //         pdf.line(amountColEnd, currentY - rowHeight, amountColEnd, currentY);

  //         pdf.setFont('helvetica', 'bold');
  //         pdf.text('remark', tableStartX + 2, currentY - 3);
  //         pdf.setFont('helvetica', 'normal');
  //         pdf.text(narration || '', labelColEnd + 2, currentY - 3);
  //         currentY += rowHeight;

  //         // Draw bottom border
  //         pdf.setLineWidth(0.5);
  //         pdf.line(tableStartX, currentY, tableEndX, currentY);
  //         pdf.line(labelColEnd, accountTableStartY, labelColEnd, currentY);
  //         pdf.line(valueColEnd, accountTableStartY, valueColEnd, currentY);
  //         pdf.line(amountColEnd, accountTableStartY, amountColEnd, currentY);
  //         // Draw left border (complete from top to bottom)
  //         pdf.line(tableStartX, accountTableStartY, tableStartX, currentY);

  //         yPosition = currentY + 5; // Add spacing between account tables
  //       });

  //       // Total row
  //       yPosition += 3;
  //       checkNewPage(12);
  //       pdf.setLineWidth(0.5);
  //       pdf.line(tableStartX, yPosition, tableEndX, yPosition);
  //       yPosition += 8;
  //       pdf.setFont('helvetica', 'bold');
  //       pdf.text('TOTAL', tableStartX, yPosition);
  //       pdf.text(`${currencySymbol}${totalCreditAmount.toFixed(2)}`, amountColEnd - 2, yPosition, { align: 'right' });
  //       yPosition += 15;
  //     }

  //     // Save PDF
  //     const fileName = `payment-details-${invoice.invoice_number}-${new Date().toISOString().split('T')[0]}.pdf`;
  //     pdf.save(fileName);
  //     toast.success('Payment details downloaded successfully');
  //   } catch (error) {
  //     console.error('Error generating payment details PDF:', error);
  //     errorModal('Failed to generate payment details PDF');
  //   }
  // };

  const handleViewDetails = async (invoice: VendorInvoice) => {
    setShowDetailModal(true);
    // First set the invoice from list (for immediate display)
    setSelectedInvoice(invoice);
    setApproverNotes({
      1: invoice.approver_1_note || '',
      2: invoice.approver_2_note || '',
      3: invoice.approver_3_note || '',
    });
    // Reset dynamic approver notes when invoice is loaded
    setDynamicApproverNotes({});
    // Initialize finance form with empty entry
    setFinanceForm([{ account_number: '', account_name: '', amount: '', narration: '' }]);
    setFinanceErrors([{}]);

    // Fetch full invoice details including approver_details
    try {
      const invoiceResponse = await vendorInvoicesAPI.getById(invoice.id);
      if (invoiceResponse.success) {
        const fullInvoiceData = invoiceResponse.data;
        setSelectedInvoice(fullInvoiceData);
        // Update approver notes from full data
        setApproverNotes({
          1: fullInvoiceData.approver_1_note || '',
          2: fullInvoiceData.approver_2_note || '',
          3: fullInvoiceData.approver_3_note || '',
        });
        // Reset dynamic approver notes
        setDynamicApproverNotes({});
        // Update finance form - handle both old format (single) and new format (array)
        let newFinanceForm: Array<{ account_number: string; account_name: string; amount: string; narration: string }>;
        if (fullInvoiceData.finance_bank_details && Array.isArray(fullInvoiceData.finance_bank_details) && fullInvoiceData.finance_bank_details.length > 0) {
          // New format: array of bank details
          newFinanceForm = fullInvoiceData.finance_bank_details.map((detail: any) => ({
            account_number: detail.account_number || '',
            account_name: detail.account_name || '',
            amount: detail.amount?.toString() || '',
            narration: detail.narration || ''
          }));
        } else if (fullInvoiceData.finance_account_no || fullInvoiceData.finance_account_name) {
          // Old format: single account (for backward compatibility)
          newFinanceForm = [{
            account_number: fullInvoiceData.finance_account_no || '',
            account_name: fullInvoiceData.finance_account_name || '',
            amount: '',
            narration: ''
          }];
        } else {
          // No existing data
          newFinanceForm = [{ account_number: '', account_name: '', amount: '', narration: '' }];
        }
        setFinanceForm(newFinanceForm);
        setFinanceErrors(newFinanceForm.map(() => ({})));
      }
    } catch (error) {
      console.error('Error fetching invoice details:', error);
      errorModal('Failed to load invoice details');
    }

    // Fetch audit trail data
    try {
      const response = await vendorInvoicesAPI.getAuditTrail(invoice.id);
      if (response.success) {
        setAuditTrailData(response.data);
      }
    } catch (error) {
      console.error('Error fetching status trail:', error);
    }
  };


  // Helper: disable Edit Invoice when any approver has approved and none have rejected
  const shouldDisableEditForInvoice = (invoice?: VendorInvoice | null): boolean => {
    if (!invoice) return false;
    const statuses = [
      invoice.approver_1_status,
      invoice.approver_2_status,
      invoice.approver_3_status,
    ] as Array<string | undefined>;
    const hasApproved = statuses.some((s) => s === 'approved');
    return hasApproved;
  };


  const handleApproverDecision = async (
    level: 1 | 2 | 3,
    decision: 'approved' | 'rejected'
  ) => {
    if (!selectedInvoice) return;
    const approvalKey = `level-${level}-${decision}`;
    try {
      setSubmittingApproval(approvalKey);
      const comments = approverNotes[level] || '';
      const resp = await vendorInvoicesAPI.setApproverStatus(
        selectedInvoice.id,
        level,
        decision,
        comments
      );
      if (resp.success) {
        toast.success(resp.message || `Level ${level} ${decision}`);
        // Update local invoice state with refreshed statuses and notes
        setSelectedInvoice((prev) =>
          prev
            ? {
              ...prev,
              approver_1_status: resp.data?.approver_1_status || prev.approver_1_status,
              approver_2_status: resp.data?.approver_2_status || prev.approver_2_status,
              approver_3_status: resp.data?.approver_3_status || prev.approver_3_status,
              ...(level === 1 ? { approver_1_note: comments } : {}),
              ...(level === 2 ? { approver_2_note: comments } : {}),
              ...(level === 3 ? { approver_3_note: comments } : {}),
            }
            : prev
        );
        refetch();
        // Close the modal on success
        setShowDetailModal(false);
      } else {
        errorModal(resp.error || 'Failed to update approver status');
      }
    } catch (err) {
      console.error('Approver decision failed:', err);
      errorModal((err as any)?.response?.data?.error || 'Failed to update approver status');
    } finally {
      setSubmittingApproval(null);
    }
  };

  // Handler for dynamic approver decisions
  const handleDynamicApproverDecision = async (
    approverId: number,
    decision: 'approved' | 'rejected'
  ) => {
    if (!selectedInvoice) return;
    const approvalKey = `${approverId}-${decision}`;
    try {
      setSubmittingApproval(approvalKey);
      const notes = dynamicApproverNotes[approverId] || '';
      const resp = await vendorInvoicesAPI.approve(selectedInvoice.id, decision, notes);
      if (resp.success) {
        toast.success(resp.message || `Invoice ${decision}`);
        // Refresh invoice data
        try {
          const invoiceResponse = await vendorInvoicesAPI.getById(selectedInvoice.id);
          if (invoiceResponse.success) {
            setSelectedInvoice(invoiceResponse.data);
            // Reset approver notes
            setDynamicApproverNotes({});
          }
        } catch (fetchError) {
          console.error('Error fetching updated invoice:', fetchError);
        }
        // Refresh audit trail
        try {
          const auditRes = await vendorInvoicesAPI.getAuditTrail(selectedInvoice.id);
          if (auditRes.success) {
            setAuditTrailData(auditRes.data);
          }
        } catch (auditError) {
          console.error('Error fetching audit trail:', auditError);
        }
        refetch();
        // Close the modal on success
        setShowDetailModal(false);
      } else {
        errorModal(resp.error || 'Failed to update approver status');
      }
    } catch (err) {
      console.error('Dynamic approver decision failed:', err);
      errorModal((err as any)?.response?.data?.error || 'Failed to update approver status');
    } finally {
      setSubmittingApproval(null);
    }
  };

  const handleFinanceFieldChange = (
    index: number,
    field: 'account_number' | 'account_name' | 'amount' | 'narration',
    value: string
  ) => {
    setFinanceForm((prev) => {
      const updated = [...prev];
      if (field === 'account_number') {
        const sanitized = value.replace(/\D/g, '').slice(0, 20);
        updated[index] = { ...updated[index], account_number: sanitized };
      } else if (field === 'account_name') {
        const sanitized = value.replace(/\s{2,}/g, ' ').replace(/^\s+/, '').slice(0, 100);
        updated[index] = { ...updated[index], account_name: sanitized };
      } else if (field === 'amount') {
        // Allow numbers and decimal point
        const sanitized = value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1');
        updated[index] = { ...updated[index], amount: sanitized };
      } else {
        const sanitized = value.slice(0, 500);
        updated[index] = { ...updated[index], narration: sanitized };
      }
      return updated;
    });

    setFinanceErrors((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: undefined };
      return updated;
    });
  };

  const handleAddBankDetail = () => {
    setFinanceForm((prev) => [...prev, { account_number: '', account_name: '', amount: '', narration: '' }]);
    setFinanceErrors((prev) => [...prev, {}]);
  };

  const handleRemoveBankDetail = (index: number) => {
    if (financeForm.length > 1) {
      setFinanceForm((prev) => prev.filter((_, i) => i !== index));
      setFinanceErrors((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleFinanceDecision = async (decision: 'approved' | 'rejected') => {
    if (!selectedInvoice) return;

    if (decision === 'approved') {
      // Validate all bank details
      const errors: Array<{ account_number?: string; account_name?: string; amount?: string; narration?: string }> = [];
      let hasValidEntry = false;

      financeForm.forEach((detail) => {
        const error: { account_number?: string; account_name?: string; amount?: string; narration?: string } = {};
        const accountNumber = detail.account_number.trim();
        const accountName = detail.account_name.trim();
        const amount = detail.amount.trim();

        // Check if this entry has any data
        if (accountNumber || accountName || amount || detail.narration.trim()) {
          if (!accountNumber) {
            error.account_number = 'Account number is required';
          } else if (!/^\d+$/.test(accountNumber)) {
            error.account_number = 'Account number must include digits only';
          } else if (accountNumber.length > 20) {
            error.account_number = 'Account number must be 20 digits or fewer';
          }

          if (!accountName) {
            error.account_name = 'Account name is required';
          } else if (accountName.length < 2) {
            error.account_name = 'Account name must be at least 2 characters';
          }

          if (!amount) {
            error.amount = 'Amount is required';
          } else {
            const amountNum = parseFloat(amount);
            if (isNaN(amountNum) || amountNum <= 0) {
              error.amount = 'Amount must be a positive number';
            }
          }

          // If no errors, this is a valid entry
          if (Object.keys(error).length === 0) {
            hasValidEntry = true;
          }
        }

        errors.push(error);
      });

      // At least one complete bank detail is required
      if (!hasValidEntry) {
        errorModal('Please provide at least one complete debit account details');
        setFinanceErrors(errors);
        return;
      }

      // Check if there are any validation errors
      const hasErrors = errors.some(err => Object.keys(err).length > 0);
      if (hasErrors) {
        setFinanceErrors(errors);
        return;
      }

      // Validate that sum of all bank detail amounts equals invoice total amount
      const validBankDetails = financeForm.filter(detail => {
        const accountNumber = detail.account_number.trim();
        const accountName = detail.account_name.trim();
        const amount = detail.amount.trim();
        return accountNumber && accountName && amount;
      });

      if (validBankDetails.length > 0) {
        const totalBankAmount = validBankDetails.reduce((sum, detail) => {
          const amount = parseFloat(detail.amount.trim());
          return sum + (isNaN(amount) ? 0 : amount);
        }, 0);

        const invoiceTotalAmount = parseFloat(selectedInvoice.total_amount?.toString() || '0');

        // Compare with a small tolerance for floating point precision (0.01)
        if (Math.abs(totalBankAmount - invoiceTotalAmount) > 0.01) {
          errorModal(
            `Sum of debit account details amounts (${formatCurrency(totalBankAmount, selectedInvoice.currency || 'GBP')}) must equal invoice total amount (${formatCurrency(invoiceTotalAmount, selectedInvoice.currency || 'GBP')})`
          );
          return;
        }
      }
    } else {
      setFinanceErrors(financeForm.map(() => ({})));
    }

    try {
      setIsSubmitting(true);
      const formData = new FormData();
      formData.append('finance_status', decision);
      if (decision === 'approved') {
        // Filter out empty entries and prepare bank details
        const bankDetails = financeForm
          .filter(detail => {
            const accountNumber = detail.account_number.trim();
            const accountName = detail.account_name.trim();
            const amount = detail.amount.trim();
            return accountNumber && accountName && amount;
          })
          .map(detail => ({
            account_number: detail.account_number.trim(),
            account_name: detail.account_name.trim(),
            amount: parseFloat(detail.amount.trim()),
            narration: detail.narration.trim() || ''
          }));

        // Send as JSON string in FormData
        formData.append('finance_bank_details', JSON.stringify(bankDetails));
      }

      const resp = await vendorInvoicesAPI.update(selectedInvoice.id, formData);

      if (resp.success) {
        toast.success(resp.message || `Finance ${decision}`);

        if (decision === 'approved') {
          // Prepare bank details for state update
          const submittedDetails = financeForm
            .filter(detail => {
              const accountNumber = detail.account_number.trim();
              const accountName = detail.account_name.trim();
              const amount = detail.amount.trim();
              return accountNumber && accountName && amount;
            })
            .map(detail => ({
              account_number: detail.account_number.trim(),
              account_name: detail.account_name.trim(),
              amount: parseFloat(detail.amount.trim()),
              narration: detail.narration.trim() || ''
            }));

          // Use response data if available, otherwise use submitted details
          const updatedInvoice = resp.data || selectedInvoice;
          const bankDetailsFromResponse = updatedInvoice?.finance_bank_details && Array.isArray(updatedInvoice.finance_bank_details)
            ? updatedInvoice.finance_bank_details
            : submittedDetails;

          // Update selectedInvoice with finance_status and finance_bank_details
          setSelectedInvoice((prev) =>
            prev
              ? {
                ...prev,
                ...(updatedInvoice || {}),
                finance_status: decision,
                finance_bank_details: bankDetailsFromResponse,
                // Keep old fields for backward compatibility if needed
                finance_account_no: bankDetailsFromResponse.length > 0 ? bankDetailsFromResponse[0].account_number : (updatedInvoice?.finance_account_no || prev.finance_account_no),
                finance_account_name: bankDetailsFromResponse.length > 0 ? bankDetailsFromResponse[0].account_name : (updatedInvoice?.finance_account_name || prev.finance_account_name),
              }
              : prev
          );

          setFinanceForm(bankDetailsFromResponse.length > 0 ? bankDetailsFromResponse.map((d: any) => ({
            account_number: d.account_number || '',
            account_name: d.account_name || '',
            amount: d.amount?.toString() || '',
            narration: d.narration || ''
          })) : [{ account_number: '', account_name: '', amount: '', narration: '' }]);
        } else {
          // For rejected, clear finance_bank_details
          const updatedInvoice = resp.data || selectedInvoice;
          setSelectedInvoice((prev) =>
            prev
              ? {
                ...prev,
                ...(updatedInvoice || {}),
                finance_status: decision,
                finance_bank_details: undefined,
              }
              : prev
          );
          setFinanceForm([{ account_number: '', account_name: '', amount: '', narration: '' }]);
        }
        refetch();
      } else {
        errorModal(resp.error || 'Failed to update finance decision');
      }
    } catch (err) {
      console.error('Finance decision failed:', err);
      errorModal((err as any)?.response?.data?.error || 'Failed to update finance decision');
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!invoices || !Array.isArray(invoices)) return;

    const map: Record<number, { amount: number; date: string }[]> = {};
    invoices.forEach((inv) => {
      const paid = parseFloat(inv?.paid_amount?.toString() || "0");
      if (paid > 0) {
        map[inv.id] = [{ amount: paid, date: inv.updated_at || new Date().toISOString() }];
      } else {
        map[inv.id] = [];
      }
    });

    setPaymentsMap(map);
  }, [invoices]);

  const handleEditDetails = (invoice: VendorInvoice) => {
    navigate(`/invoices/${invoice.id}`);
  };

  const statusDropdown = [
    {
      label: 'All',
      value: 'all'
    },
    {
      label: 'Pending',
      value: 'pending'
    },
    {
      label: 'Level 1 Approved',
      value: 'level_1_approved'
    },
    {
      label: 'Level 2 Approved',
      value: 'level_2_approved'
    },
    {
      label: 'Level 3 Approved',
      value: 'level_3_approved'
    },
    {
      label: 'Paid',
      value: 'paid'
    },
    {
      label: 'Rejected',
      value: 'rejected'
    },
    {
      label: 'Overdue',
      value: 'overdue'
    },

  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vendor Invoices</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage invoices received from your vendors.
          </p>
        </div>
        {userPermissions === "invoice_initiator" && (
          <div className="flex space-x-3">
            <button
              onClick={() => setShowUploadModal(true)}
              className="btn-primary flex items-center"
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload Invoice
            </button>
          </div>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center min-w-0">
              <div className="p-2 bg-primary-100 rounded-lg flex-shrink-0">
                <FileText className="h-6 w-6 text-primary-600" />
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-600 truncate">Total Invoices</p>
                <p className="text-2xl font-bold text-gray-900 break-words">{invoiceStats?.total_invoices ?? 0}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center min-w-0">
              <div className="p-2 bg-green-100 rounded-lg flex-shrink-0">
                <Check className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-600 truncate">Paid</p>
                <p className="text-2xl font-bold text-gray-900 break-words">{invoiceStats?.paid_invoices ?? 0}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center min-w-0">
              <div className="p-2 bg-yellow-100 rounded-lg flex-shrink-0">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-600 truncate">Pending</p>
                <p className="text-2xl font-bold text-gray-900 break-words">{invoiceStats?.pending_invoices ?? 0}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center min-w-0">
              <div className="p-2 bg-red-100 rounded-lg flex-shrink-0">
                <AlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-4 min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-600 truncate">Overdue</p>
                <p className="text-2xl font-bold text-gray-900 break-words">{invoiceStats?.overdue_invoices ?? 0}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-3 sm:space-y-0">
            <div className="flex-1">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="input-field pl-10"
                  placeholder="Search invoices or vendors..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div className="flex space-x-3">
              <select
                className="input-field appearance-none w-full pr-8"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {statusDropdown.map((status) => (
                  <option key={status.value} value={status.value}>{status.label}</option>
                ))}
              </select>
              {/* <button className="btn-secondary flex items-center">
                <Filter className="h-4 w-4 mr-2" />
                Filter
              </button> */}
            </div>
          </div>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold text-gray-900">
            Vendor Invoices
          </h3>
        </div>
        <div className="card-body p-0">
          {error ? (
            <div className="rounded-lg bg-red-50 p-4 border border-red-200 m-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-red-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Error loading vendor invoices</h3>
                  <div className="mt-2 text-sm text-red-700">{error}</div>
                </div>
              </div>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No invoices found</h3>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">Invoice #</th>
                    <th className="table-header-cell">Vendor</th>
                    <th className="table-header-cell">Amount</th>
                    <th className="table-header-cell">Status</th>
                    <th className="table-header-cell">Invoice Date</th>
                    <th className="table-header-cell">Due Date</th>
                    <th className="table-header-cell">Actions</th>
                  </tr>
                </thead>
                <tbody className="table-body">
                  {filteredInvoices.map((invoice) => {

                    const payments = paymentsMap[invoice.id] || [];
                    const totalAmount = parseFloat(invoice.total_amount.toString());
                    const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
                    const remainingAmount = totalAmount - totalPaid;

                    return (
                      <tr key={invoice.id} className="table-row">
                        <td className="table-cell">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {invoice.invoice_number || 'N/A'}
                            </div>
                          </div>
                        </td>
                        <td className="table-cell">
                          <div className="text-sm text-gray-900">
                            {invoice.vendor_name || 'N/A'}
                          </div>
                        </td>
                        <td className="table-cell">
                          <div>
                            <div className={`text-sm font-medium text-gray-900 ${getCurrencyFontClass(invoice.currency || 'GBP')}`}>
                              {formatCurrency(invoice.total_amount, invoice.currency || 'GBP')}
                            </div>
                            {invoice.paid_amount > 0 && (
                              <div className={`text-sm text-gray-500 ${getCurrencyFontClass(invoice.currency || 'GBP')}`}>
                                {formatCurrency(invoice.paid_amount, invoice.currency || 'GBP')} paid
                                {/* {formatCurrency(totalPaid, invoice.currency || 'GBP')} paid */}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="table-cell">
                          {getStatusBadge(invoice.status)}
                        </td>
                        <td className="table-cell">
                          <div className="text-sm text-gray-900">
                            {new Date(invoice.invoice_date).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="table-cell">
                          <div className="text-sm text-gray-900">
                            {new Date(invoice.due_date).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="table-cell">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => {
                                handleViewDetails(invoice)
                              }}
                              className="text-primary-600 hover:text-primary-800"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            {/* <button
                              onClick={() => {
                                setSelectedInvoice(invoice);
                                setShowChartModal(true);
                              }}
                              className="text-green-600 hover:text-green-800"
                              title="View Vendor Progress Chart"
                            >
                              <BarChart3 className="h-4 w-4" />
                            </button> */}
                            {/* <button
                              onClick={() => {
                                setSelectedInvoice(invoice);
                                fetchBanks(invoice.vendor_id);
                                setStatusForm({ status: invoice.status, notes: invoice.notes || '' });
                                setShowStatusModal(true);
                                setSelectedBankId(invoice.bank_id?.toString() || '');
                              }}
                              className="text-primary-600 hover:text-primary-800"
                              title="Update Status"
                            >
                              <Edit className="h-4 w-4" />
                            </button> */}
                            {userPermissions === "payment_approver" && invoice.status != 'paid' && (
                              <button
                                onClick={() => {
                                  setSelectedInvoice(invoice);

                                  setPaymentForm({
                                    paid_amount: remainingAmount.toFixed(2), // remaining balance
                                    payment_date: new Date().toISOString().split('T')[0],
                                    notes: '',
                                    reference_number: '',
                                    broucher: null
                                  });

                                  setShowPaymentModal(true);
                                }}
                                className="text-green-600 hover:text-green-800"
                                title="Mark as Paid"
                              >
                                <Check className="h-4 w-4" />
                              </button>
                            )}
                            {/* {invoice.status === 'paid' || invoice.status === 'pending at payment processor' && (
                              <button
                                onClick={() => handleDownloadPaymentDetails(invoice)}
                                className="text-blue-600 hover:text-blue-800"
                                title="Download Payment Details"
                              >
                                <Download className="h-4 w-4" />
                              </button>
                            )} */}
                            {/* {invoice.attachment_filename && (
                              <button
                                onClick={() => handleViewMainAttachment(invoice)}
                                className="text-gray-600 hover:text-gray-800"
                                title="View Attachment"
                              >
                                <Eye className="h-4 w-4" />
                              </button>
                            )} */}
                            {invoice.status === 'pending' && (
                              <button
                                onClick={() => handleDelete(invoice)}
                                className="text-red-600 hover:text-red-800"
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              {pagination && (
                <Pagination
                  pagination={{
                    page: pagination.page,
                    limit: pagination.limit,
                    total: pagination.total,
                    total_pages: pagination.total_pages,
                  }}
                  onPageChange={(p) => setPage(p)}
                  onLimitChange={(l) => {
                    setLimit(Math.min(100, Math.max(1, l)));
                    setPage(1);
                  }}
                  loading={loading}
                  className="px-4 pb-4"
                />
              )}
            </div>
          )}
        </div>
      </div>

      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" style={{ margin: '0px !important' }}>
          <div className="relative w-full max-w-2xl bg-white rounded-xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-white">Upload Vendor Invoice</h3>
                <button
                  onClick={handleCloseModal}
                  className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors">
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <form onSubmit={handleUploadSubmit} className="space-y-4">
                {/* <div className="grid grid-cols-1 sm:grid-cols-2 gap-4"> */}
                {/* <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Department *</label>
                    <div ref={departmentDropdownRef} className="relative">
                      <button
                        type="button"
                        onClick={() => {
                          setDepartmentDropdownOpen(!departmentDropdownOpen);
                        }}
                        className="input-field w-full flex items-center justify-between text-left"
                      >
                        <span className={uploadForm.department_id ? 'text-gray-900' : 'text-gray-500'}>
                          {uploadForm.department_id
                            ? departmentDropdown.find(d => d.id.toString() === uploadForm.department_id)?.department || 'Select Department'
                            : 'Select Department'}
                        </span>
                        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${departmentDropdownOpen ? 'transform rotate-180' : ''}`} />
                      </button>
                      {departmentDropdownOpen && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
                          <div className="p-2 border-b border-gray-200">
                            <div className="relative">
                              <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                              <input
                                type="text"
                                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                                placeholder="Search departments..."
                                value={departmentSearchTerm}
                                onChange={(e) => setDepartmentSearchTerm(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            {departmentDropdown
                              .filter(dept => 
                                dept.department.toLowerCase().includes(departmentSearchTerm.toLowerCase())
                              )
                              .map((dept) => (
                                <button
                                  key={dept.id}
                                  type="button"
                                  className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-primary-50 hover:text-primary-900 focus:bg-primary-50 focus:text-primary-900 focus:outline-none"
                                  onClick={() => {
                                    setUploadForm({ ...uploadForm, department_id: dept.id.toString(), vendor_id: '' });
                                    setDepartmentDropdownOpen(false);
                                    setDepartmentSearchTerm('');
                                  }}
                                >
                                  {dept.department}
                                </button>
                              ))}
                            {departmentDropdown.filter(dept => 
                              dept.department.toLowerCase().includes(departmentSearchTerm.toLowerCase())
                            ).length === 0 && (
                              <div className="px-4 py-2 text-sm text-gray-500 text-center">
                                No departments found
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div> */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Approval Type</label>
                  <div ref={approvalTypeDropdownRef} className="relative">
                    <button
                      type="button"
                      onClick={() => {
                        setApprovalTypeDropdownOpen(!approvalTypeDropdownOpen);
                        // setDepartmentDropdownOpen(false);
                      }}
                      className="input-field w-full flex items-center justify-between text-left"
                    >
                      <span className={uploadForm.approval_type ? 'text-gray-900' : 'text-gray-500'}>
                        {uploadForm.approval_type || 'Select Approval Type'}
                      </span>
                      <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${approvalTypeDropdownOpen ? 'transform rotate-180' : ''}`} />
                    </button>
                    {approvalTypeDropdownOpen && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
                        <div className="max-h-48 overflow-y-auto">
                          {approvalTypeOptions.map((option, index) => (
                            <button
                              key={index}
                              type="button"
                              className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-primary-50 hover:text-primary-900 focus:bg-primary-50 focus:text-primary-900 focus:outline-none"
                              onClick={() => {
                                setUploadForm({ ...uploadForm, approval_type: option, proposal: '' });
                                setApprovalTypeDropdownOpen(false);
                              }}
                            >
                              {option}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {errors.approval_type && <p className="text-red-500 text-xs mt-1">{errors.approval_type}</p>}
                </div>
                {uploadForm.approval_type === 'Invoice Payment - Financial approval available' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Select Proposal *</label>
                    <div ref={proposalDropdownRef} className="relative">
                      <button
                        type="button"
                        onClick={() => {
                          setProposalDropdownOpen(!proposalDropdownOpen);
                          setApprovalTypeDropdownOpen(false);
                          if (!proposalDropdownOpen) {
                            setProposalSearchTerm('');
                          }
                          // setDepartmentDropdownOpen(false);
                        }}
                        className={`input-field w-full flex items-center justify-between text-left ${errors.proposal ? 'border-red-500' : ''}`}
                      >
                        <span className={uploadForm.proposal ? 'text-gray-900' : 'text-gray-500'}>
                          {uploadForm.proposal
                            ? (() => {
                              const selected = financialPowersDropdown.find(item => item.id.toString() === uploadForm.proposal);
                              return selected ? `${selected.vendorName} - ${selected.referenceNumber} - ${selected.referenceName}` : 'Select Proposal';
                            })()
                            : 'Select Proposal'}
                        </span>
                        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${proposalDropdownOpen ? 'transform rotate-180' : ''}`} />
                      </button>
                      {proposalDropdownOpen && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
                          <div className="p-2 border-b border-gray-200">
                            <div className="relative">
                              <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                              <input
                                type="text"
                                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                                placeholder="Search proposals..."
                                value={proposalSearchTerm}
                                onChange={(e) => setProposalSearchTerm(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            {loadingFinancialPowers ? (
                              <div className="px-4 py-2 text-sm text-gray-500 text-center">
                                Loading...
                              </div>
                            ) : financialPowersDropdown.filter(item =>
                              item.vendorName.toLowerCase().includes(proposalSearchTerm.toLowerCase()) ||
                              item.referenceNumber.toLowerCase().includes(proposalSearchTerm.toLowerCase()) ||
                              item.referenceName.toLowerCase().includes(proposalSearchTerm.toLowerCase())
                            ).length > 0 ? (
                              financialPowersDropdown
                                .filter(item =>
                                  item.vendorName.toLowerCase().includes(proposalSearchTerm.toLowerCase()) ||
                                  item.referenceNumber.toLowerCase().includes(proposalSearchTerm.toLowerCase()) ||
                                  item.referenceName.toLowerCase().includes(proposalSearchTerm.toLowerCase())
                                )
                                .map((item) => (
                                  <button
                                    key={item.id}
                                    type="button"
                                    className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-primary-50 hover:text-primary-900 focus:bg-primary-50 focus:text-primary-900 focus:outline-none"
                                    onClick={() => {
                                      setUploadForm({ ...uploadForm, proposal: item.id.toString() });
                                      setProposalDropdownOpen(false);
                                      setProposalSearchTerm('');
                                    }}
                                  >
                                    <div className="font-medium">{item.vendorName}</div>
                                    <div className="text-xs text-gray-500">{item.referenceNumber} - {item.referenceName}</div>
                                  </button>
                                ))
                            ) : (
                              <div className="px-4 py-2 text-sm text-gray-500 text-center">
                                {proposalSearchTerm ? 'No proposals found matching your search' : 'No proposals found'}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    {errors.proposal && <p className="text-red-500 text-xs mt-1">{errors.proposal}</p>}
                  </div>
                )}
                {/* <div>
                    <label className="block text-sm font-medium text-gray-700">Vendor *</label>
                    <div ref={vendorDropdownRef} className="relative">
                      <button
                        type="button"
                        onClick={() => {
                          setDepartmentDropdownOpen(false);
                        }}
                        className={`input-field w-full flex items-center justify-between text-left ${errors.vendor_id ? 'border-red-500' : ''}`}
                      >
                        <span className={uploadForm.vendor_id ? 'text-gray-900' : 'text-gray-500'}>
                          {uploadForm.vendor_id
                            ? vendors?.find(v => v.id.toString() === uploadForm.vendor_id)?.name || 'Select vendor'
                            : 'Select vendor'}
                        </span>
                        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${vendorDropdownOpen ? 'transform rotate-180' : ''}`} />
                      </button>
                      {vendorDropdownOpen && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
                          <div className="p-2 border-b border-gray-200">
                            <div className="relative">
                              <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                              <input
                                type="text"
                                className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                                placeholder="Search vendors..."
                                value={vendorSearchTerm}
                                onChange={(e) => setVendorSearchTerm(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            {vendors && vendors.length > 0 ? (
                              vendors
                                .filter(vendor => 
                                  vendor.name.toLowerCase().includes(vendorSearchTerm.toLowerCase())
                                )
                                .map((vendor) => (
                                  <button
                                    key={vendor.id}
                                    type="button"
                                    className="w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-primary-50 hover:text-primary-900 focus:bg-primary-50 focus:text-primary-900 focus:outline-none"
                                    onClick={() => {
                                      handleVendorChange({ target: { value: vendor.id.toString() } } as React.ChangeEvent<HTMLSelectElement>);
                                      setVendorSearchTerm('');
                                    }}
                                  >
                                    {vendor.name}
                                  </button>
                                ))
                            ) : (
                              <div className="px-4 py-2 text-sm text-gray-500 text-center">
                                {uploadForm.department_id ? 'No vendors found for this department' : 'Please select a department first'}
                              </div>
                            )}
                            {vendors && vendors.length > 0 && vendors.filter(vendor => 
                              vendor.name.toLowerCase().includes(vendorSearchTerm.toLowerCase())
                            ).length === 0 && (
                              <div className="px-4 py-2 text-sm text-gray-500 text-center">
                                No vendors found
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    {errors.vendor_id && <p className="text-red-500 text-xs mt-1">{errors.vendor_id}</p>}
                  </div> */}
                {/* <div>
                    <label className="block text-sm font-medium text-gray-700">Registration Number</label>
                    <input
                      type="text"
                      className={`input-field ${errors.registration_number || validationMessages.registration_number ? 'border-red-500' : ''}`}
                      value={uploadForm.registration_number || ''}
                      onChange={(e) => setUploadForm({ ...uploadForm, registration_number: e.target.value })}
                      placeholder="Enter vendor registration number"
                    />
                    {errors.registration_number && <p className="text-red-500 text-xs mt-1">{errors.registration_number}</p>}
                    {validationMessages.registration_number && !errors.registration_number && (
                      <p className="text-red-500 text-xs mt-1">{validationMessages.registration_number}</p>
                    )}
                  </div> */}
                {/* <div>
                    <label className="block text-sm font-medium text-gray-700">Invoice Number *</label>
                    <input
                      type="text"
                      className={`input-field ${errors.invoice_number || validationMessages.invoice_number ? 'border-red-500' : ''}`}
                      value={uploadForm.invoice_number}
                      onChange={(e) => setUploadForm({ ...uploadForm, invoice_number: e.target.value })}
                      placeholder="Enter invoice number"
                    />
                    {errors.invoice_number && <p className="text-red-500 text-xs mt-1">{errors.invoice_number}</p>}
                    {validationMessages.invoice_number && !errors.invoice_number && (
                      <p className="text-red-500 text-xs mt-1">{validationMessages.invoice_number}</p>
                    )}
                  </div> */}
                {/* <div>
                    <label className="block text-sm font-medium text-gray-700">Invoice Date *</label>
                    <input
                      type="date"
                      className={`input-field ${errors.invoice_date ? 'border-red-500' : ''}`}
                      value={uploadForm.invoice_date}
                      onChange={handleInvoiceDateChange}
                    />
                    {errors.invoice_date && <p className="text-red-500 text-xs mt-1">{errors.invoice_date}</p>}
                  </div> */}
                {/* <div>
                    <label className="block text-sm font-medium text-gray-700">Due Date *</label>
                    <input
                      type="date"
                      className={`input-field ${errors.due_date || validationMessages.due_date ? 'border-red-500' : ''}`}
                      value={uploadForm.due_date}
                      min={uploadForm.invoice_date}
                      onChange={handleDueDateChange}
                    />
                    {errors.due_date && <p className="text-red-500 text-xs mt-1">{errors.due_date}</p>}
                    {validationMessages.due_date && !errors.due_date && (
                      <p className="text-red-500 text-xs mt-1">{validationMessages.due_date}</p>
                    )}
                  </div> */}
                {/* <div>
                    <label className="block text-sm font-medium text-gray-700">Amount (Excluding Vat/Tax) *</label>
                    <input
                      type="number"
                      step="0.01"
                      className={`input-field ${errors.subtotal ? 'border-red-500' : ''}`}
                      value={uploadForm.subtotal}
                      onChange={(e) => setUploadForm({ ...uploadForm, subtotal: e.target.value })}
                      placeholder="Enter amount (excluding vat/tax)"
                    />
                    {errors.subtotal && <p className="text-red-500 text-xs mt-1">{errors.subtotal}</p>}
                  </div> */}
                {/* <div>
                    <label className="block text-sm font-medium text-gray-700">Vat/Tax Amount *</label>
                    <input
                      type="number"
                      step="0.01"
                      className={`input-field ${errors.tax_amount ? 'border-red-500' : ''}`}
                      value={uploadForm.tax_amount}
                      onChange={(e) => setUploadForm({ ...uploadForm, tax_amount: e.target.value })}
                      placeholder="Enter vat/tax amount"
                    />
                    {errors.tax_amount && <p className="text-red-500 text-xs mt-1">{errors.tax_amount}</p>}
                  </div> */}
                {/* <div>
                    <label className="block text-sm font-medium text-gray-700">Total Amount *</label>
                    <input
                      type="number"
                      step="0.01"
                      className={`input-field ${errors.total_amount ? 'border-red-500' : ''}`}
                      value={uploadForm.total_amount}
                      onChange={(e) => setUploadForm({ ...uploadForm, total_amount: e.target.value })}
                      placeholder="Enter total amount"
                    />
                    {errors.total_amount && <p className="text-red-500 text-xs mt-1">{errors.total_amount}</p>}
                  </div> */}
                {/* <div>
                    <label className="block text-sm font-medium text-gray-700">PO Number</label>
                    <input
                      type="text"
                      className={`input-field ${errors.po_number ? 'border-red-500' : ''}`}
                      value={uploadForm.po_number}
                      onChange={(e) => setUploadForm({ ...uploadForm, po_number: e.target.value })}
                      placeholder="Enter po number"
                    />
                    {errors.po_number && <p className="text-red-500 text-xs mt-1">{errors.po_number}</p>}
                  </div> */}
                {/* </div> */}
                {/* <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    rows={3}
                    className={`input-field ${errors.description ? 'border-red-500' : ''}`}
                    value={uploadForm.description}
                    onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                  />
                  {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
                </div> */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Invoice File *</label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf"
                    className={`input-field ${errors.file ? 'border-red-500' : ''}`}
                    onChange={(e) => setUploadForm({ ...uploadForm, file: e.target.files?.[0] || null })}
                  />
                  <p className="text-xs text-gray-500 mt-1">Supported formats: PDF (Max 10MB)</p>
                  {errors.file && <p className="text-red-500 text-xs mt-1">{errors.file}</p>}
                </div>
                {/* <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="use_ocr"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    checked={uploadForm.use_ocr}
                    onChange={(e) => setUploadForm({ ...uploadForm, use_ocr: e.target.checked })}
                  />
                  <label htmlFor="use_ocr" className="ml-2 block text-sm text-gray-900">
                    Use OCR to automatically extract data from the uploaded file
                  </label>
                </div> */}
                <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="btn-secondary w-full sm:w-auto"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`btn-primary w-full sm:w-auto ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {isSubmitting ? 'Uploading...' : 'Upload Invoice'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Detail Modal */}
      {showDetailModal && selectedInvoice && (
        <div style={{ margin: '0px !important' }}>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="relative w-full max-w-5xl bg-white rounded-xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                      <FileText className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white">Invoice Details</h3>
                      <p className="text-primary-100 text-sm">#{selectedInvoice.invoice_number}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setShowDetailModal(false);
                      closeAttachmentViewer();
                    }}
                    className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-4 sm:p-6">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                  {/* Left Column - Basic Info */}
                  <div className="lg:col-span-2 space-y-4 sm:space-y-6">
                    {/* Invoice Overview Card */}
                    <div className="bg-gray-50 rounded-lg p-4 sm:p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="text-lg font-semibold text-gray-900">Invoice Overview</h4>
                        <div className="flex items-center space-x-2">
                          {getStatusBadge(selectedInvoice.status)}
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-3">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                              <FileText className="h-4 w-4 text-primary-600" />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide">Invoice Number</p>
                              <p className="text-sm font-medium text-gray-900">{selectedInvoice.invoice_number}</p>
                            </div>
                          </div>
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                              <Calendar className="h-4 w-4 text-purple-600" />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide">Invoice Date</p>
                              <p className="text-sm font-medium text-gray-900">{new Date(selectedInvoice.invoice_date).toLocaleDateString()}</p>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                              <Building className="h-4 w-4 text-green-600" />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide">Vendor</p>
                              <p className="text-sm font-medium text-gray-900">{selectedInvoice.vendor_name || 'N/A'}</p>
                            </div>
                          </div>
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center mr-3">
                              <Calendar className="h-4 w-4 text-orange-600" />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 uppercase tracking-wide">Due Date</p>
                              <p className="text-sm font-medium text-gray-900">{new Date(selectedInvoice.due_date).toLocaleDateString()}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Financial Summary Card */}
                    <div className="bg-gradient-to-r from-green-50 to-primary-50 rounded-lg p-4 sm:p-6 border border-green-100">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Financial Summary</h4>

                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {selectedInvoice.subtotal && (
                          <div className="text-center">
                            <div className={`text-lg font-semibold text-gray-700 ${getCurrencyFontClass(selectedInvoice.currency || 'GBP')}`}>
                              {formatCurrency(selectedInvoice.subtotal, selectedInvoice.currency || 'GBP')}
                            </div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Amount (Excluding Vat/Tax)</p>
                          </div>
                        )}
                        {selectedInvoice.tax_amount && (
                          <div className="text-center">
                            <div className={`text-lg font-semibold text-gray-700 ${getCurrencyFontClass(selectedInvoice.currency || 'GBP')}`}>
                              {formatCurrency(selectedInvoice.tax_amount, selectedInvoice.currency || 'GBP')}
                            </div>
                            <p className="text-xs text-gray-500 uppercase tracking-wide">Vat/Tax Amount</p>
                          </div>
                        )}
                        <div className="text-center">
                          <div className={`text-2xl font-semibold text-gray-700 ${getCurrencyFontClass(selectedInvoice.currency || 'GBP')}`}>
                            {formatCurrency(selectedInvoice.total_amount, selectedInvoice.currency || 'GBP')}
                          </div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Total Amount</p>
                        </div>
                      </div>

                      {selectedInvoice.paid_amount > 0 && (
                        <div className="mt-4 pt-4 border-t border-green-200">
                          <div className="flex items-center justify-center">
                            <Check className="h-5 w-5 text-green-600 mr-2" />
                            <span className={`text-sm font-medium text-green-700 ${getCurrencyFontClass(selectedInvoice.currency || 'GBP')}`}>
                              {formatCurrency(selectedInvoice.paid_amount, selectedInvoice.currency || 'GBP')} Paid
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Line Items */}
                    {(selectedInvoice.line_items && selectedInvoice.line_items.length > 0) ? (
                      <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">

                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Description</th>
                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Qty</th>
                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Unit Price</th>
                                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">Amount</th>

                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {(
                                selectedInvoice.line_items?.map((item, index) => (
                                  <tr key={item.id || index} className="hover:bg-gray-50">
                                    <td className="px-3 py-2 text-sm text-gray-900">{item.description}</td>
                                    <td className="px-3 py-2 text-sm text-gray-900 text-right">{item.quantity}</td>
                                    <td className={`px-3 py-2 text-sm text-gray-900 text-right ${getCurrencyFontClass(selectedInvoice.currency || 'GBP')}`}>
                                      {formatCurrency(item.unit_price, selectedInvoice.currency || 'GBP')}
                                    </td>
                                    <td className={`px-3 py-2 text-sm font-medium text-gray-900 text-right ${getCurrencyFontClass(selectedInvoice.currency || 'GBP')}`}>
                                      {formatCurrency(item.amount, selectedInvoice.currency || 'GBP')}
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ) : null}

                    {/* Invoice Initiation Details */}
                    {(selectedInvoice) && (
                      <div className="bg-gradient-to-br from-white to-gray-50 border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                        {/* Header */}
                        <div className="px-4 py-3">
                          <div className="flex items-center">
                            <div>
                              <h4 className="text-lg font-semibold text-gray-900">Invoice Initiation</h4>
                            </div>
                          </div>
                        </div>

                        {/* Content */}
                        <div className="p-6">
                          <div className="space-y-6">
                            {/* PO Number */}
                            {selectedInvoice.po_number && (
                              <div className="group">
                                <div className="flex items-center p-4 bg-white rounded-lg border border-gray-200 hover:border-indigo-300 hover:shadow-md transition-all duration-200">
                                  <div className="flex-1">
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">PO Number</p>
                                    <p className="text-sm font-semibold text-gray-900">{selectedInvoice.po_number}</p>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Approver Notes */}
                            {selectedInvoice.initiator_note && (
                              <div className="group">
                                <div className="p-4 bg-gradient-to-br from-primary-50 to-primary-50 rounded-lg border border-primary-200 hover:border-primary-300 hover:shadow-md transition-all duration-200">
                                  <div className="flex items-start">
                                    <div className="flex-1">
                                      <p className="text-xs font-semibold text-primary-700 uppercase tracking-wider mb-2">Initiator Notes</p>
                                      <p className="text-sm text-gray-800 leading-relaxed">{selectedInvoice.initiator_note}</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Payment Notes */}
                            {/* {selectedInvoice.payment_notes && (
                            <div className="group">
                              <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border border-green-200 hover:border-green-300 hover:shadow-md transition-all duration-200">
                                <div className="flex items-start">
                                  <div className="flex-1">
                                    <p className="text-xs font-semibold text-green-700 uppercase tracking-wider mb-2">Payment Processor Notes</p>
                                    <p className="text-sm text-gray-800 leading-relaxed">{selectedInvoice.payment_notes}</p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )} */}

                            {/* Approver Level 1 */}
                            {selectedInvoice.approver_1 && (
                              <div className="group">
                                <div className={`p-4 rounded-lg border hover:shadow-md transition-all duration-200 ${selectedInvoice.approver_1_status === 'approved' ? 'bg-green-50 border-green-200 hover:border-green-300' : 'bg-gradient-to-br from-orange-50 to-pink-50 border-orange-200 hover:border-orange-300'}`}>
                                  <div className="flex items-center justify-between mb-3">
                                    <div>
                                      <p className="text-xs font-semibold text-orange-700 uppercase tracking-wider">Level 1 Approver</p>
                                    </div>
                                    <span className={`text-xs font-semibold capitalize ${selectedInvoice.approver_1_status === 'approved' ? 'text-green-700' : selectedInvoice.approver_1_status === 'rejected' ? 'text-red-700' : 'text-gray-600'}`}>
                                      {selectedInvoice.approver_1_status || 'pending'}
                                    </span>
                                  </div>
                                  <div className={`bg-white rounded-lg p-3 border ${selectedInvoice.approver_1_status === 'approved' ? 'border-green-100' : 'border-orange-100'}`}>
                                    {(() => {
                                      const approver = invoiceApprovers?.find(u => u.id.toString() === selectedInvoice.approver_1?.toString());
                                      return approver && (
                                        <div className="flex items-center">
                                          <div className={`${selectedInvoice.approver_1_status === 'approved' ? 'bg-green-100' : 'bg-gradient-to-br from-orange-100 to-orange-200'} w-8 h-8 rounded-full flex items-center justify-center mr-3`}>
                                            <span className={`text-xs font-semibold ${selectedInvoice.approver_1_status === 'approved' ? 'text-green-700' : 'text-orange-700'}`}>
                                              {approver.first_name.charAt(0)}{approver.last_name.charAt(0)}
                                            </span>
                                          </div>
                                          <div>
                                            <p className="text-sm font-semibold text-gray-900">{approver.first_name} {approver.last_name}</p>
                                            <p className="text-xs text-gray-600">{approver.email}</p>
                                          </div>
                                        </div>
                                      )
                                    })()}
                                  </div>

                                  {/* Note area */}
                                  {String(user?.id || '') === String(selectedInvoice.approver_1 || '') && (selectedInvoice.approver_1_status !== 'approved' && selectedInvoice.approver_1_status !== 'rejected') ? (
                                    <div className="mt-3 space-y-2">
                                      <label className="block text-xs font-medium text-gray-700">Note</label>
                                      <textarea
                                        rows={3}
                                        className="input-field"
                                        value={approverNotes[1]}
                                        onChange={(e) => setApproverNotes({ ...approverNotes, 1: e.target.value })}
                                      />
                                      <div className="flex items-center space-x-2">
                                        <button
                                          type="button"
                                          disabled={submittingApproval === 'level-1-rejected' || submittingApproval === 'level-1-approved'}
                                          onClick={() => handleApproverDecision(1, 'rejected')}
                                          className="px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                        >
                                          {submittingApproval === 'level-1-rejected' && (
                                            <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                          )}
                                          {submittingApproval === 'level-1-rejected' ? 'Rejecting...' : 'Reject'}
                                        </button>
                                        <button
                                          type="button"
                                          disabled={submittingApproval === 'level-1-approved' || submittingApproval === 'level-1-rejected'}
                                          onClick={() => handleApproverDecision(1, 'approved')}
                                          className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                        >
                                          {submittingApproval === 'level-1-approved' && (
                                            <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                          )}
                                          {submittingApproval === 'level-1-approved' ? 'Approving...' : 'Approve'}
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    selectedInvoice.approver_1_note ? (
                                      <div className="mt-3 p-3 bg-white border rounded">
                                        <p className="text-xs font-semibold text-gray-700 mb-1">Level 1 Approver</p>
                                        <p className="text-sm text-gray-800">{selectedInvoice.approver_1_note}</p>
                                      </div>
                                    ) : null
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Approver Level 2 */}
                            {selectedInvoice.approver_2 && (
                              <div className="group">
                                <div className={`p-4 rounded-lg border hover:shadow-md transition-all duration-200 ${selectedInvoice.approver_2_status === 'approved' ? 'bg-green-50 border-green-200 hover:border-green-300' : 'bg-gradient-to-br from-orange-50 to-red-50 border-orange-200 hover:border-orange-300'}`}>
                                  <div className="flex items-center justify-between mb-3">
                                    <div>
                                      <p className="text-xs font-semibold text-orange-700 uppercase tracking-wider">Level 2 Approver</p>
                                    </div>
                                    <span className={`text-xs font-semibold capitalize ${selectedInvoice.approver_2_status === 'approved' ? 'text-green-700' : selectedInvoice.approver_2_status === 'rejected' ? 'text-red-700' : 'text-gray-600'}`}>
                                      {selectedInvoice.approver_2_status || 'pending'}
                                    </span>
                                  </div>
                                  <div className={`bg-white rounded-lg p-3 border ${selectedInvoice.approver_2_status === 'approved' ? 'border-green-100' : 'border-orange-100'}`}>
                                    {(() => {
                                      const approver = invoiceApprovers?.find(u => u.id.toString() === selectedInvoice.approver_2?.toString());
                                      return approver && (
                                        <div className="flex items-center">
                                          <div className={`${selectedInvoice.approver_2_status === 'approved' ? 'bg-green-100' : 'bg-gradient-to-br from-orange-100 to-orange-200'} w-8 h-8 rounded-full flex items-center justify-center mr-3`}>
                                            <span className={`text-xs font-semibold ${selectedInvoice.approver_2_status === 'approved' ? 'text-green-700' : 'text-orange-700'}`}>
                                              {approver.first_name.charAt(0)}{approver.last_name.charAt(0)}
                                            </span>
                                          </div>
                                          <div>
                                            <p className="text-sm font-semibold text-gray-900">{approver.first_name} {approver.last_name}</p>
                                            <p className="text-xs text-gray-600">{approver.email}</p>
                                          </div>
                                        </div>
                                      )
                                    })()}
                                  </div>

                                  {/* Level 2 note and actions (only for its approver) */}
                                  {String(user?.id || '') === String(selectedInvoice.approver_2 || '') && (selectedInvoice.approver_2_status !== 'approved' && selectedInvoice.approver_2_status !== 'rejected') ? (
                                    <div className="mt-3 space-y-2">
                                      <label className="block text-xs font-medium text-gray-700">Note</label>
                                      <textarea
                                        rows={3}
                                        className="input-field"
                                        value={approverNotes[2]}
                                        onChange={(e) => setApproverNotes({ ...approverNotes, 2: e.target.value })}
                                      />
                                      <div className="flex items-center space-x-2">
                                        <button
                                          type="button"
                                          disabled={submittingApproval === 'level-2-rejected' || submittingApproval === 'level-2-approved'}
                                          onClick={() => handleApproverDecision(2, 'rejected')}
                                          className="px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                        >
                                          {submittingApproval === 'level-2-rejected' && (
                                            <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                          )}
                                          {submittingApproval === 'level-2-rejected' ? 'Rejecting...' : 'Reject'}
                                        </button>
                                        <button
                                          type="button"
                                          disabled={submittingApproval === 'level-2-approved' || submittingApproval === 'level-2-rejected'}
                                          onClick={() => handleApproverDecision(2, 'approved')}
                                          className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                        >
                                          {submittingApproval === 'level-2-approved' && (
                                            <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                          )}
                                          {submittingApproval === 'level-2-approved' ? 'Approving...' : 'Approve'}
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    selectedInvoice.approver_2_note ? (
                                      <div className="mt-3 p-3 bg-white border rounded">
                                        <p className="text-xs font-semibold text-gray-700 mb-1">Approver 2 Note</p>
                                        <p className="text-sm text-gray-800">{selectedInvoice.approver_2_note}</p>
                                      </div>
                                    ) : null
                                  )}
                                </div>
                              </div>
                            )}
                            {/* Approver Level 3 */}
                            {selectedInvoice.approver_3 && (
                              <div className="group">
                                <div className={`p-4 rounded-lg border hover:shadow-md transition-all duration-200 ${selectedInvoice.approver_3_status === 'approved' ? 'bg-green-50 border-green-200 hover:border-green-300' : 'bg-gradient-to-br from-orange-50 to-red-50 border-orange-200 hover:border-orange-300'}`}>
                                  <div className="flex items-center justify-between mb-3">
                                    <div>
                                      <p className="text-xs font-semibold text-orange-700 uppercase tracking-wider">Level 3 Approver</p>
                                    </div>
                                    <span className={`text-xs font-semibold capitalize ${selectedInvoice.approver_3_status === 'approved' ? 'text-green-700' : selectedInvoice.approver_3_status === 'rejected' ? 'text-red-700' : 'text-gray-600'}`}>
                                      {selectedInvoice.approver_3_status || 'pending'}
                                    </span>
                                  </div>
                                  <div className={`bg-white rounded-lg p-3 border ${selectedInvoice.approver_3_status === 'approved' ? 'border-green-100' : 'border-orange-100'}`}>
                                    {(() => {
                                      const approver = invoiceApprovers?.find(u => u.id.toString() === selectedInvoice.approver_3?.toString());
                                      return approver && (
                                        <div className="flex items-center">
                                          <div className={`${selectedInvoice.approver_3_status === 'approved' ? 'bg-green-100' : 'bg-gradient-to-br from-orange-100 to-orange-200'} w-8 h-8 rounded-full flex items-center justify-center mr-3`}>
                                            <span className={`text-xs font-semibold ${selectedInvoice.approver_3_status === 'approved' ? 'text-green-700' : 'text-orange-700'}`}>
                                              {approver.first_name.charAt(0)}{approver.last_name.charAt(0)}
                                            </span>
                                          </div>
                                          <div>
                                            <p className="text-sm font-semibold text-gray-900">{approver.first_name} {approver.last_name}</p>
                                            <p className="text-xs text-gray-600">{approver.email}</p>
                                          </div>
                                        </div>
                                      )
                                    })()}
                                  </div>

                                  {/* Level 3 note and actions */}
                                  {String(user?.id || '') === String(selectedInvoice.approver_3 || '') && (selectedInvoice.approver_3_status !== 'approved' && selectedInvoice.approver_3_status !== 'rejected') ? (
                                    <div className="mt-3 space-y-2">
                                      <label className="block text-xs font-medium text-gray-700">Note</label>
                                      <textarea
                                        rows={3}
                                        className="input-field"
                                        value={approverNotes[3]}
                                        onChange={(e) => setApproverNotes({ ...approverNotes, 3: e.target.value })}
                                      />
                                      <div className="flex items-center space-x-2">
                                        <button
                                          type="button"
                                          disabled={submittingApproval === 'level-3-rejected' || submittingApproval === 'level-3-approved'}
                                          onClick={() => handleApproverDecision(3, 'rejected')}
                                          className="px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                        >
                                          {submittingApproval === 'level-3-rejected' && (
                                            <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                          )}
                                          {submittingApproval === 'level-3-rejected' ? 'Rejecting...' : 'Reject'}
                                        </button>
                                        <button
                                          type="button"
                                          disabled={submittingApproval === 'level-3-approved' || submittingApproval === 'level-3-rejected'}
                                          onClick={() => handleApproverDecision(3, 'approved')}
                                          className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                        >
                                          {submittingApproval === 'level-3-approved' && (
                                            <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                          )}
                                          {submittingApproval === 'level-3-approved' ? 'Approving...' : 'Approve'}
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    selectedInvoice.approver_3_note ? (
                                      <div className="mt-3 p-3 bg-white border rounded">
                                        <p className="text-xs font-semibold text-gray-700 mb-1">Approver 3 Note</p>
                                        <p className="text-sm text-gray-800">{selectedInvoice.approver_3_note}</p>
                                      </div>
                                    ) : null
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Account Details Section - Nastro and Credit Accounts */}
                            {selectedInvoice && (
                              <div className="space-y-4">
                                {/* Nastro Accounts */}
                                {(selectedInvoice as any).nastro_accounts && Array.isArray((selectedInvoice as any).nastro_accounts) && (selectedInvoice as any).nastro_accounts.length > 0 && (
                                  <div className="group">
                                    <div className="p-4 rounded-lg border hover:shadow-md transition-all duration-200 bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200 hover:border-purple-300">
                                      <div className="flex items-center justify-between mb-3">
                                        <div>
                                          <p className="text-xs font-semibold text-purple-700 uppercase tracking-wider">Nostro Accounts</p>
                                        </div>
                                      </div>
                                      <div className="space-y-3">
                                        {(selectedInvoice as any).nastro_accounts.map((account: any, index: number) => (
                                          <div key={index} className="bg-white rounded-lg p-3 border border-purple-100">
                                            <p className="text-xs font-semibold text-purple-600 uppercase tracking-wider mb-2">Nostro Detail {index + 1}</p>
                                            <div className="space-y-2">
                                              <div>
                                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Account Number</p>
                                                <p className="text-sm font-semibold text-gray-900">{account.account_no || 'N/A'}</p>
                                              </div>
                                              <div>
                                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Account Name</p>
                                                <p className="text-sm font-semibold text-gray-900">{account.account_name || 'N/A'}</p>
                                              </div>
                                              {account.narration && (
                                                <div>
                                                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Narration</p>
                                                  <p className="text-sm text-gray-900">{account.narration}</p>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Credit Accounts */}
                                {(selectedInvoice as any).credit_accounts && Array.isArray((selectedInvoice as any).credit_accounts) && (selectedInvoice as any).credit_accounts.length > 0 && (
                                  <div className="group">
                                    <div className="p-4 rounded-lg border hover:shadow-md transition-all duration-200 bg-gradient-to-br from-green-50 to-green-100 border-green-200 hover:border-green-300">
                                      <div className="flex items-center justify-between mb-3">
                                        <div>
                                          <p className="text-xs font-semibold text-green-700 uppercase tracking-wider">Credit Accounts</p>
                                        </div>
                                      </div>
                                      <div className="space-y-3">
                                        {(selectedInvoice as any).credit_accounts.map((account: any, index: number) => (
                                          <div key={index} className="bg-white rounded-lg p-3 border border-green-100">
                                            <p className="text-xs font-semibold text-green-600 uppercase tracking-wider mb-2">Credit Detail {index + 1}</p>
                                            <div className="space-y-2">
                                              <div>
                                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Bank Name</p>
                                                <p className="text-sm font-semibold text-gray-900">{account.bank_name || 'N/A'}</p>
                                              </div>
                                              <div>
                                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Beneficiary Name</p>
                                                <p className="text-sm font-semibold text-gray-900">{account.beneficiary_name || 'N/A'}</p>
                                              </div>
                                              <div>
                                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Account Number</p>
                                                <p className="text-sm font-semibold text-gray-900">{account.account_no || 'N/A'}</p>
                                              </div>
                                              {account.sort_code && (
                                                <div>
                                                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Sort Code</p>
                                                  <p className="text-sm font-semibold text-gray-900">{account.sort_code}</p>
                                                </div>
                                              )}
                                              {account.iban_no && (
                                                <div>
                                                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">IBAN No</p>
                                                  <p className="text-sm font-semibold text-gray-900">{account.iban_no}</p>
                                                </div>
                                              )}
                                              {account.bic && (
                                                <div>
                                                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">BIC</p>
                                                  <p className="text-sm font-semibold text-gray-900">{account.bic}</p>
                                                </div>
                                              )}
                                              {account.amount && (
                                                <div>
                                                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Amount</p>
                                                  <p className={`text-sm font-semibold text-gray-900 ${getCurrencyFontClass(selectedInvoice.currency || 'GBP')}`}>
                                                    {formatCurrency(parseFloat(account.amount.toString()), selectedInvoice.currency || 'GBP')}
                                                  </p>
                                                </div>
                                              )}
                                              {account.narration && (
                                                <div>
                                                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Narration</p>
                                                  <p className="text-sm text-gray-900">{account.narration}</p>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Finance Team Review */}
                            {(() => {
                              // Check if using static approvers (legacy)
                              const pending = selectedInvoice.status !== 'pending';
                              // const staticApproverApproved = selectedInvoice.approver_3_status === 'approved';
                              // const staticFinancePending = selectedInvoice.status === 'pending at finance team';
                              // const staticApproverPending = selectedInvoice.status === 'pending at approver';

                              // // Check if using dynamic approvers - all must be approved and none rejected
                              // const dynamicApproversAllApproved = selectedInvoice.approver_details &&
                              //   selectedInvoice.approver_details.length > 0 &&
                              //   selectedInvoice.approver_details.every((approver: any) => approver.status === 'approved') &&
                              //   !selectedInvoice.approver_details.some((approver: any) => approver.status === 'rejected');

                              return pending;
                            })() && (
                                <div className="group">
                                  <div className={`p-4 rounded-lg border hover:shadow-md transition-all duration-200 ${selectedInvoice.finance_status === 'approved'
                                    ? 'bg-green-50 border-green-200 hover:border-green-300'
                                    : selectedInvoice.finance_status === 'rejected'
                                      ? 'bg-red-50 border-red-200 hover:border-red-300'
                                      : 'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200 hover:border-blue-300'
                                    }`}>
                                    <div className="flex items-center justify-between mb-3">
                                      <div>
                                        <p className="text-xs font-semibold text-blue-700 uppercase tracking-wider">Finance Team</p>
                                      </div>
                                      <span className={`text-xs font-semibold capitalize ${selectedInvoice.finance_status === 'approved'
                                        ? 'text-green-700'
                                        : selectedInvoice.finance_status === 'rejected'
                                          ? 'text-red-700'
                                          : 'text-gray-600'
                                        }`}>
                                        {selectedInvoice.finance_status || 'pending'}
                                      </span>
                                    </div>
                                    <div className={`bg-white rounded-lg p-3 border ${selectedInvoice.finance_status === 'approved'
                                      ? 'border-green-100'
                                      : selectedInvoice.finance_status === 'rejected'
                                        ? 'border-red-100'
                                        : 'border-blue-100'
                                      }`}>
                                      {userPermissions === 'finance_team' && selectedInvoice.finance_status !== 'approved' && selectedInvoice.finance_status !== 'rejected' ? (
                                        <div className="space-y-4">
                                          {financeForm.map((detail, index) => (
                                            <div key={index} className="p-3 border border-gray-200 rounded-lg bg-gray-50">
                                              <div className="flex items-center justify-between mb-3">
                                                <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                                                  Debit Account Detail {index + 1}
                                                </p>
                                                {financeForm.length > 1 && (
                                                  <button
                                                    type="button"
                                                    onClick={() => handleRemoveBankDetail(index)}
                                                    className="text-red-600 hover:text-red-800 p-1"
                                                    title="Remove this debit account detail"
                                                  >
                                                    <X className="h-4 w-4" />
                                                  </button>
                                                )}
                                              </div>

                                              <div className="space-y-3">
                                                <div>
                                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Account Number <span className="text-red-500">*</span>
                                                  </label>
                                                  <input
                                                    type="text"
                                                    inputMode="numeric"
                                                    pattern="[0-9]*"
                                                    className="input-field"
                                                    value={detail.account_number}
                                                    onChange={(e) => handleFinanceFieldChange(index, 'account_number', e.target.value)}
                                                    placeholder="Enter account number"
                                                  />
                                                  {financeErrors[index]?.account_number && (
                                                    <p className="text-xs text-red-600 mt-1">{financeErrors[index].account_number}</p>
                                                  )}
                                                </div>

                                                <div>
                                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Account Name <span className="text-red-500">*</span>
                                                  </label>
                                                  <input
                                                    type="text"
                                                    className="input-field"
                                                    value={detail.account_name}
                                                    onChange={(e) => handleFinanceFieldChange(index, 'account_name', e.target.value)}
                                                    placeholder="Enter account name"
                                                  />
                                                  {financeErrors[index]?.account_name && (
                                                    <p className="text-xs text-red-600 mt-1">{financeErrors[index].account_name}</p>
                                                  )}
                                                </div>

                                                <div>
                                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Amount <span className="text-red-500">*</span>
                                                  </label>
                                                  <input
                                                    type="text"
                                                    inputMode="decimal"
                                                    className="input-field"
                                                    value={detail.amount}
                                                    onChange={(e) => handleFinanceFieldChange(index, 'amount', e.target.value)}
                                                    placeholder="Enter amount"
                                                  />
                                                  {financeErrors[index]?.amount && (
                                                    <p className="text-xs text-red-600 mt-1">{financeErrors[index].amount}</p>
                                                  )}
                                                </div>

                                                <div>
                                                  <label className="block text-xs font-medium text-gray-700 mb-1">
                                                    Narration
                                                  </label>
                                                  <textarea
                                                    rows={2}
                                                    className="input-field"
                                                    value={detail.narration}
                                                    onChange={(e) => handleFinanceFieldChange(index, 'narration', e.target.value)}
                                                    placeholder="Enter narration (optional)"
                                                  />
                                                </div>
                                              </div>
                                            </div>
                                          ))}

                                          <button
                                            type="button"
                                            onClick={handleAddBankDetail}
                                            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
                                          >
                                            <Plus className="h-4 w-4" />
                                            Add More Debit Account Details
                                          </button>

                                          <div className="flex items-center space-x-2 pt-2 border-t border-gray-200">
                                            <button
                                              type="button"
                                              disabled={isSubmitting}
                                              onClick={() => handleFinanceDecision('rejected')}
                                              className="px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed"
                                            >
                                              Reject
                                            </button>
                                            <button
                                              type="button"
                                              disabled={isSubmitting}
                                              onClick={() => handleFinanceDecision('approved')}
                                              className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed"
                                            >
                                              Approve
                                            </button>
                                          </div>
                                        </div>
                                      ) : (
                                        <div className="space-y-3">
                                          {(() => {
                                            // Handle both old format (single) and new format (array)
                                            const bankDetails = selectedInvoice.finance_bank_details && Array.isArray(selectedInvoice.finance_bank_details) && selectedInvoice.finance_bank_details.length > 0
                                              ? selectedInvoice.finance_bank_details
                                              : (selectedInvoice.finance_account_no || selectedInvoice.finance_account_name)
                                                ? [{
                                                  account_number: selectedInvoice.finance_account_no || '',
                                                  account_name: selectedInvoice.finance_account_name || '',
                                                  amount: '',
                                                  narration: ''
                                                }]
                                                : [];

                                            if (bankDetails.length === 0) {
                                              return <p className="text-sm text-gray-500">Awaiting finance team</p>;
                                            }

                                            return bankDetails.map((detail: any, index: number) => (
                                              <div key={index} className="p-3 border border-gray-200 rounded-lg bg-gray-50">
                                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                                                  Debit Account Detail {index + 1}
                                                </p>
                                                <div className="space-y-2">
                                                  <div>
                                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Account Number</p>
                                                    <p className="text-sm font-semibold text-gray-900">
                                                      {detail.account_number || 'N/A'}
                                                    </p>
                                                  </div>
                                                  <div>
                                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Account Name</p>
                                                    <p className="text-sm font-semibold text-gray-900">
                                                      {detail.account_name || 'N/A'}
                                                    </p>
                                                  </div>
                                                  {detail.amount && (
                                                    <div>
                                                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Amount</p>
                                                      <p className={`text-sm font-semibold text-gray-900 ${getCurrencyFontClass(selectedInvoice.currency || 'GBP')}`}>
                                                        {formatCurrency(detail.amount, selectedInvoice.currency || 'GBP')}
                                                      </p>
                                                    </div>
                                                  )}
                                                  {detail.narration && (
                                                    <div>
                                                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Narration</p>
                                                      <p className="text-sm text-gray-900">
                                                        {detail.narration}
                                                      </p>
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                            ));
                                          })()}
                                          {selectedInvoice.finance_status === 'rejected' && (
                                            <p className="text-xs text-red-600">Finance team rejected this invoice.</p>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}
                          </div>


                            {/* Dynamic Approver Flow - Separate section outside Invoice Initiation */}
                            {selectedInvoice.approver_details && (
                              <div className="pt-6">
                                <div className="space-y-4">
                                  {selectedInvoice.approver_details.map((approver: any, index: number) => {
                                    const isCurrentUserApprover = String(user?.id || '') === String(approver.approver_id || '');
                                    const canApprove = isCurrentUserApprover &&
                                      approver.status !== 'approved' &&
                                      approver.status !== 'rejected';
                                    const isApproved = approver.status === 'approved';
                                    const isRejected = approver.status === 'rejected';

                                    return (
                                      <div
                                        key={approver.id || index}
                                        className={`p-4 rounded-lg border transition-all duration-200 ${isApproved
                                            ? 'bg-green-50 border-green-200 hover:border-green-300'
                                            : isRejected
                                              ? 'bg-red-50 border-red-200 hover:border-red-300'
                                              : 'bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200 hover:border-orange-300'
                                          }`}
                                      >
                                        <div className="flex items-center justify-between mb-3">
                                          <div>
                                            <p className="text-xs font-semibold text-orange-700 uppercase tracking-wider">
                                              LEVEL {index + 1} APPROVER
                                            </p>
                                          </div>
                                          <span
                                            className={`text-xs font-semibold capitalize ${isApproved
                                                ? 'text-green-700'
                                                : isRejected
                                                  ? 'text-red-700'
                                                  : 'text-gray-600'
                                              }`}
                                          >
                                            {approver.status || 'Pending'}
                                          </span>
                                        </div>
                                        <div
                                          className={`bg-white rounded-lg p-3 border ${isApproved
                                              ? 'border-green-100'
                                              : isRejected
                                                ? 'border-red-100'
                                                : 'border-orange-100'
                                            }`}
                                        >
                                          <div className="flex items-center">
                                            <div
                                              className={`${isApproved
                                                  ? 'bg-green-100'
                                                  : isRejected
                                                    ? 'bg-red-100'
                                                    : 'bg-gradient-to-br from-orange-100 to-orange-200'
                                                } w-8 h-8 rounded-full flex items-center justify-center mr-3`}
                                            >
                                              <span
                                                className={`text-xs font-semibold ${isApproved
                                                    ? 'text-green-700'
                                                    : isRejected
                                                      ? 'text-red-700'
                                                      : 'text-orange-700'
                                                  }`}
                                              >
                                                {approver.first_name?.charAt(0) || ''}
                                                {approver.last_name?.charAt(0) || ''}
                                              </span>
                                            </div>
                                            <div>
                                              <p className="text-sm font-semibold text-gray-900">
                                                {approver.first_name} {approver.last_name}
                                              </p>
                                              <p className="text-xs text-gray-600">{approver.email}</p>
                                            </div>
                                          </div>
                                        </div>

                                        {/* Note and action area */}
                                        {canApprove ? (
                                          <div className="mt-3 space-y-2">
                                            <label className="block text-xs font-medium text-gray-700">Note</label>
                                            <textarea
                                              rows={3}
                                              className="input-field w-full"
                                              value={dynamicApproverNotes[approver.approver_id] || ''}
                                              onChange={(e) => {
                                                setDynamicApproverNotes(prev => ({
                                                  ...prev,
                                                  [approver.approver_id]: e.target.value
                                                }));
                                              }}
                                              placeholder="Enter your note..."
                                            />

                                            <div className="flex items-center space-x-2">
                                              <button
                                                type="button"
                                                disabled={submittingApproval === `${approver.approver_id}-rejected` || submittingApproval === `${approver.approver_id}-approved`}
                                                onClick={() => handleDynamicApproverDecision(approver.approver_id, 'rejected')}
                                                className="px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                              >
                                                {submittingApproval === `${approver.approver_id}-rejected` && (
                                                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                  </svg>
                                                )}
                                                {submittingApproval === `${approver.approver_id}-rejected` ? 'Rejecting...' : 'Reject'}
                                              </button>
                                              <button
                                                type="button"
                                                disabled={submittingApproval === `${approver.approver_id}-approved` || submittingApproval === `${approver.approver_id}-rejected`}
                                                onClick={() => handleDynamicApproverDecision(approver.approver_id, 'approved')}
                                                className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                              >
                                                {submittingApproval === `${approver.approver_id}-approved` && (
                                                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                  </svg>
                                                )}
                                                {submittingApproval === `${approver.approver_id}-approved` ? 'Approving...' : 'Approve'}
                                              </button>
                                            </div>
                                          </div>
                                        ) : approver.notes ? (
                                          <div className="mt-3 p-3 bg-white border rounded">
                                            <p className="text-xs font-semibold text-gray-700 mb-1">Note</p>
                                            <p className="text-sm text-gray-800">{approver.notes}</p>
                                          </div>
                                        ) : null}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                          {/* Additional Attachments */}
                          {Array.isArray(selectedInvoice.additional_attachments) && selectedInvoice.additional_attachments.length > 0 && (
                            <div className="mt-8 pt-6 border-t border-gray-200">
                              <div className="flex items-center mb-4">
                                <div className="w-8 h-8 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg flex items-center justify-center mr-3">
                                  <Upload className="h-4 w-4 text-gray-600" />
                                </div>
                                <div>
                                  <h5 className="text-sm font-semibold text-gray-900">Additional Attachments</h5>
                                  <p className="text-xs text-gray-500">{selectedInvoice.additional_attachments.length} file(s)</p>
                                </div>
                              </div>

                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {selectedInvoice.additional_attachments.map((att, idx) => (
                                  <div key={`${att.filename}-${idx}`} className="group">
                                    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-indigo-300 hover:shadow-md transition-all duration-200">
                                      <div className="flex items-center flex-1 min-w-0">
                                        <div className="w-8 h-8 bg-gradient-to-br from-indigo-100 to-indigo-200 rounded-lg flex items-center justify-center mr-3">
                                          <FileText className="h-4 w-4 text-indigo-600" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                          <p className="text-sm font-medium text-gray-900 truncate">{att.filename}</p>
                                          <p className="text-xs text-gray-500">{att.size ? `${(att.size / 1024).toFixed(1)} KB` : att.type || 'Attachment'}</p>
                                        </div>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => handleViewAttachment(
                                          (att as any).url || '',
                                          att.filename,
                                          false,
                                          selectedInvoice.id
                                        )}
                                        disabled={loadingAttachment}
                                        className="ml-3 px-3 py-1.5 text-xs font-medium bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 hover:text-indigo-800 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                                      >
                                        {loadingAttachment ? (
                                          <>
                                            <Spinner size="sm" color="blue" />
                                            <span className="ml-1">Loading...</span>
                                          </>
                                        ) : (
                                          <>
                                            <Eye className="h-4 w-4 mr-1" />
                                            <span>View</span>
                                          </>
                                        )}
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}




                    {/* Payment Approver */}
                    {/* {userPermissions != 'payment_approver' && userPermissions === 'invoice_approver' && (
                    <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Payment Approver</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-1 gap-4">
                        <div>
                          <select
                            className="input-field"
                            value={editInvoiceForm.payment_approver || selectedInvoice.payment_approver?.toString?.() || ''}
                            onChange={(e) => setEditInvoiceForm({ ...editInvoiceForm, payment_approver: e.target.value })}
                            disabled={!isEditingInvoice}
                          >
                            <option value="">Select user</option>
                            {paymentApprovers?.map(u => (
                              <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.email})</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  )} */}

                  </div>

                  {/* Right Column - Actions & Attachments */}
                  <div className="space-y-4 sm:space-y-6">
                    {/* Quick Actions */}
                    <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h4>

                      <div className="space-y-3">
                        {userPermissions === "invoice_initiator" && (selectedInvoice?.status === 'pending' || selectedInvoice?.status === 'pending at finance team') && (
                          <button
                            onClick={() => {
                              handleEditDetails(selectedInvoice);
                            }}
                            disabled={shouldDisableEditForInvoice(selectedInvoice)}
                            className={`w-full flex items-center justify-center px-4 py-2 rounded-lg transition-colors ${shouldDisableEditForInvoice(selectedInvoice) ? 'bg-purple-400 text-white cursor-not-allowed' : 'bg-purple-600 text-white hover:bg-purple-700'}`}
                          >
                            <Edit className="h-4 w-4 mr-2" />
                            Edit Invoice
                          </button>
                        )}
                        {/* {(selectedInvoice?.status === 'pending at payment processor' || selectedInvoice?.status === "paid") && (
                          <button
                            onClick={() => handleDownloadPaymentDetails(selectedInvoice)}
                            className={`w-full flex items-center justify-center px-4 py-2 rounded-lg transition-colors bg-blue-600 text-white hover:bg-blue-700`}
                          >
                            <Download className="h-4 w-4 mr-2" />
                            Download Digital Voucher
                          </button>
                        )} */}

                        {/* <button
                        onClick={() => {
                          setSelectedInvoice(selectedInvoice);
                          setStatusForm({ status: selectedInvoice.status, notes: selectedInvoice.notes || '' });
                          setShowStatusModal(true);
                          setShowDetailModal(false);
                          setSelectedBankId(selectedInvoice.bank_id?.toString() || '');
                        }}
                        className="w-full flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Update Status
                      </button> */}

                        {userPermissions === "payment_approver" && selectedInvoice?.status != 'paid' && (

                          <button
                            onClick={() => {
                              setSelectedInvoice(selectedInvoice);
                              setPaymentForm({
                                paid_amount: selectedInvoice.total_amount.toString(),
                                payment_date: new Date().toISOString().split('T')[0],
                                notes: '',
                                reference_number: '',
                                broucher: null
                              });
                              setShowPaymentModal(true);
                              setShowDetailModal(false);
                            }}
                            className="w-full flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                          >
                            <Check className="h-4 w-4 mr-2" />
                            Record Payment
                          </button>
                        )}
                        {selectedInvoice.attachment_filename && (
                          <button
                            onClick={() => handleViewMainAttachment(selectedInvoice)}
                            className="w-full flex items-center justify-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View Invoice
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Attachment */}
                    {/* {(selectedInvoice.attachment_filename || isEditingInvoice) && (
                    <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Attachment</h4>

                      <div className="bg-gray-50 rounded-lg p-4 border-2 border-dashed border-gray-300">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                            <FileText className="h-5 w-5 text-primary-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {selectedInvoice.attachment_filename}
                            </p>
                            <p className="text-xs text-gray-500">
                              {selectedInvoice.attachment_size ? `${(selectedInvoice.attachment_size / 1024).toFixed(1)} KB` : 'File attached'}
                            </p>
                          </div>
                        </div>
                      </div>

                    </div>
                  )} */}

                    {/* OCR Data */}
                    {/* {selectedInvoice.ocr_data && (
                    <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">OCR Extraction Data</h4>

                      <div className="bg-gray-50 rounded-lg p-4 border">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Confidence Score</span>
                          <span className="text-xs font-medium text-gray-900">
                            {selectedInvoice.ocr_confidence ? `${(selectedInvoice.ocr_confidence * 100).toFixed(1)}%` : 'N/A'}
                          </span>
                        </div>

                        <details className="text-xs">
                          <summary className="cursor-pointer text-primary-600 hover:text-primary-800 font-medium">
                            View Raw OCR Data
                          </summary>
                          <pre className="mt-2 text-xs text-gray-700 bg-white p-3 rounded border overflow-auto max-h-40">
                            {JSON.stringify(selectedInvoice.ocr_data, null, 2)}
                          </pre>
                        </details>
                      </div>
                    </div>
                  )} */}
                    {/* {selectedInvoice?.bank_details && userPermissions === 'payment_approver' && (
                    <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                      <h4 className="text-lg font-semibold text-gray-900 mb-4">Bank Details</h4>

                      <div className="bg-gray-50 rounded-lg p-4 border">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Bank Name</span>
                          <span className="text-xs font-medium text-gray-900">
                            {selectedInvoice?.bank_details?.bank_name || 'N/A'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Account Holder Name</span>
                          <span className="text-xs font-medium text-gray-900">
                            {selectedInvoice?.bank_details?.customer_name || 'N/A'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Account Number</span>
                          <span className="text-xs font-medium text-gray-900">
                            {selectedInvoice?.bank_details?.account_number || 'N/A'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Account Type</span>
                          <span className="text-xs font-medium text-gray-900">
                            {selectedInvoice?.bank_details?.account_type || 'N/A'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">IFSC Code</span>
                          <span className="text-xs font-medium text-gray-900">
                            {selectedInvoice?.bank_details?.ifsc_code || 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )} */}

                    {/* Invoice Status Trail */}
                    {auditTrailData && auditTrailData.audit_trail && auditTrailData.audit_trail.length > 0 && (
                      <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">Invoice Status</h4>

                        <div className="relative">
                          <div className="space-y-3">
                            {auditTrailData.audit_trail
                              .slice()
                              .reverse()
                              .map((trail: any, index: number, reversedArray: any[]) => {
                                // Check if this trail item contains "Rejected" keyword
                                const statusText = trail.action === 'UPDATE' && trail.description.includes('Status changed')
                                  ? trail.details.new_status
                                  : trail.description;
                                const isRejected = statusText && statusText.toLowerCase().includes('rejected');

                                return (
                                  <div key={trail.id} className="relative flex items-start">
                                    {/* Timeline line segment - connects to next item (below current item) */}
                                    {index < reversedArray.length - 1 && (
                                      <div className={`absolute left-3 top-6 w-0.5 z-0 ${isRejected ? 'bg-red-600' : 'bg-green-300'}`} style={{ height: 'calc(100% + 0.75rem)' }}></div>
                                    )}
                                    {/* Timeline dot */}
                                    <div className={`relative z-10 flex items-center justify-center w-6 h-6 rounded-full border-2 border-white shadow-sm ${isRejected ? 'bg-red-600' : 'bg-green-600'}`}>
                                    </div>

                                    {/* Content */}
                                    <div className={`ml-3 flex-1 rounded-md p-3 border ${isRejected ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
                                      <div className="flex items-center justify-between mb-1">
                                        <span className={`text-sm font-semibold capitalize ${isRejected ? 'text-red-900' : 'text-gray-900'}`}>
                                          {statusText}
                                        </span>
                                        <span className="text-xs text-gray-500">
                                          {new Date(trail.performed_at).toLocaleString()}
                                        </span>
                                      </div>

                                      <div className="text-xs text-gray-600 mb-1">
                                        By: {trail.performed_by.name} ({trail.performed_by.email})
                                      </div>

                                      {/* Show approver details when approvers are assigned */}
                                      {trail.action === 'UPDATE' && trail.description === 'Approvers assigned' && trail.details && (
                                        <div className="space-y-0.5">
                                          {trail.details.approver_1?.new && (
                                            <div className="text-xs text-gray-600">
                                              <span className="font-medium">Approver 1:</span> {trail.details.approver_1.new.name}
                                            </div>
                                          )}
                                          {trail.details.approver_2?.new && (
                                            <div className="text-xs text-gray-600">
                                              <span className="font-medium">Approver 2:</span> {trail.details.approver_2.new.name}
                                            </div>
                                          )}
                                          {trail.details.approver_3?.new && (
                                            <div className="text-xs text-gray-600">
                                              <span className="font-medium">Approver 3:</span> {trail.details.approver_3.new.name}
                                            </div>
                                          )}
                                          {trail.details.payment_approver?.new && (
                                            <div className="text-xs text-gray-600">
                                              <span className="font-medium">Payment Approver:</span> {trail.details.payment_approver.new.name}
                                            </div>
                                          )}
                                          {trail.details.payment_attachement && (
                                            <div className="text-xs text-gray-600 flex items-center gap-2">
                                              <button
                                                type="button"
                                                onClick={() => handleDownloadAttachment(
                                                  trail.details.payment_attachement.filepath || trail.details.payment_attachement.attachment_url,
                                                  trail.details.payment_attachement.filename
                                                )}
                                                className="font-medium text-primary-600 hover:text-primary-800 underline"
                                              >
                                                Voucher:
                                              </button>
                                              <span>{trail.details.payment_attachement.new?.filepath || trail.details.payment_attachement.filename}</span>
                                            </div>
                                          )}
                                        </div>
                                      )}

                                      {(trail.details.paid_amount || trail.details.reference_number || trail.details.payment_date || trail.details.payment_notes) && (
                                        <div className="mt-2 grid grid-cols-1 md:grid-cols-1 gap-2">
                                          {trail.details.paid_amount && (
                                            <div className="flex flex-col bg-green-50 rounded border-l-4 border-green-400 px-3 py-2">
                                              <span className="text-xs text-green-700 font-semibold">Paid Amount</span>
                                              <span className="text-sm text-green-900">{trail.details.paid_amount}</span>
                                            </div>
                                          )}
                                          {trail.details.reference_number && (
                                            <div className="flex flex-col bg-yellow-50 rounded border-l-4 border-yellow-400 px-3 py-2">
                                              <span className="text-xs text-yellow-700 font-semibold">Transaction ID</span>
                                              <span className="text-sm text-yellow-900">{trail.details.reference_number}</span>
                                            </div>
                                          )}
                                          {trail.details.payment_date && (
                                            <div className="flex flex-col bg-primary-50 rounded border-l-4 border-primary-400 px-3 py-2">
                                              <span className="text-xs text-primary-700 font-semibold">Payment Date</span>
                                              <span className="text-sm text-primary-900">
                                                {trail.details.payment_date ? new Date(trail.details.payment_date).toLocaleDateString() : ''}
                                              </span>
                                            </div>
                                          )}
                                          {trail.details.notes && (
                                            <div className="flex flex-col bg-gray-50 rounded border-l-4 border-gray-400 px-3 py-2">
                                              <span className="text-xs text-gray-700 font-semibold">Notes</span>
                                              <span className="text-sm text-gray-900">{trail.details.notes}</span>
                                            </div>
                                          )}
                                          {trail.details.payment_attachement?.attachment_url && (
                                            <button
                                              type="button"
                                              onClick={() => handleDownloadAttachment(
                                                trail.details.payment_attachement.attachment_url,
                                                trail.details.payment_attachement.filename
                                              )}
                                              className="flex flex-col bg-gray-50 rounded border-l-4 border-gray-400 px-3 py-2 text-left hover:bg-gray-100"
                                              title="View payment attachment"
                                            >
                                              <span className="text-xs text-gray-700 font-semibold">Voucher</span>
                                              <span className="text-sm text-primary-600 underline truncate">{trail.details.payment_attachement.filename}</span>
                                            </button>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        </div>

                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      {showStatusModal && selectedInvoice && (
        <div style={{ margin: '0px !important' }}>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="relative w-full max-w-lg bg-white rounded-xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-white">Update Status</h3>
                  <button
                    onClick={() => setShowStatusModal(false)}
                    className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <form onSubmit={handleStatusUpdate} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status *</label>
                    <select
                      required
                      className="input-field"
                      value={statusForm.status}
                      onChange={(e) => setStatusForm({ ...statusForm, status: e.target.value })}
                    >
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                      <option value="paid">Paid</option>
                      <option value="overdue">Overdue</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </div>

                  {/* Conditional Bank Selection for Approvers when approving */}
                  {statusForm.status === 'approved' && userPermissions != 'payment_approver' && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <label className="block text-sm font-medium text-gray-700">Bank Account</label>
                        <button
                          type="button"
                          className="text-sm text-primary-600 hover:text-primary-800"
                          onClick={() => {
                            setShowAddBank(!showAddBank);
                            setBankError('');
                          }}
                        >
                          {showAddBank ? 'Select from list' : 'Add new bank'}
                        </button>
                      </div>

                      {!showAddBank && (
                        <div>
                          <select
                            className="input-field"
                            value={selectedBankId}
                            onChange={(e) => {
                              setSelectedBankId(e.target.value);
                              setBankError('');
                            }}
                          >
                            <option value="">Select bank</option>
                            {bankOptions.map((b) => (
                              <option key={b.id} value={String(b.id)}>
                                {`${b?.bank_name || 'Bank'} (${b.account_number || ''})`}
                              </option>
                            ))}
                          </select>
                          {bankError && (
                            <p className="mt-1 text-sm text-red-600">{bankError}</p>
                          )}
                        </div>
                      )}

                      {showAddBank && (
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Bank Name</label>
                            <input
                              className="input-field"
                              value={addBankForm.bank_name}
                              onChange={(e) => setAddBankForm({ ...addBankForm, bank_name: e.target.value })}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Bank Holder Name</label>
                            <input
                              className="input-field"
                              value={addBankForm.account_holder_name}
                              onChange={(e) => setAddBankForm({ ...addBankForm, account_holder_name: e.target.value })}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Account Number</label>
                            <input
                              className="input-field"
                              value={addBankForm.account_number}
                              onChange={(e) => setAddBankForm({ ...addBankForm, account_number: e.target.value })}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">Account Type</label>
                            <input
                              className="input-field"
                              value={addBankForm.account_type}
                              onChange={(e) => setAddBankForm({ ...addBankForm, account_type: e.target.value })}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700">IFSC Code</label>
                            <input
                              className="input-field"
                              value={addBankForm.ifsc_code}
                              onChange={(e) => setAddBankForm({ ...addBankForm, ifsc_code: e.target.value })}
                            />
                          </div>
                          {bankError && (
                            <p className="mt-1 text-sm text-red-600">{bankError}</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Notes</label>
                    <textarea
                      rows={3}
                      className="input-field"
                      value={statusForm.notes}
                      onChange={(e) => setStatusForm({ ...statusForm, notes: e.target.value })}
                    />
                  </div>

                  <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => setShowStatusModal(false)}
                      className="btn-secondary w-full sm:w-auto"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-primary w-full sm:w-auto"
                    >
                      {isSubmitting ? 'Updating...' : 'Update Status'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedInvoice && (
        <div style={{ margin: '0px !important' }}>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="relative w-full max-w-lg bg-white rounded-xl shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="bg-gradient-to-r from-green-600 to-green-700 px-6 py-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-white">Payment Processing Details</h3>
                  <button
                    onClick={handleClosePaymentModal}
                    className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <form onSubmit={handleMarkPaid} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Paid Amount *</label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      className="input-field"
                      value={paymentForm.paid_amount}
                      disabled
                      onChange={(e) => setPaymentForm({ ...paymentForm, paid_amount: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Payment Date *</label>
                    <input
                      type="date"
                      required
                      className="input-field"
                      value={paymentForm.payment_date}
                      onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Transaction ID *</label>
                    <input
                      className="input-field"
                      value={paymentForm.reference_number}
                      placeholder="Transaction ID..."
                      onChange={(e) => {
                        const value = e.target.value;
                        setPaymentForm({ ...paymentForm, reference_number: value });
                        if (paymentErrors.reference_number) {
                          setPaymentErrors((prev) => {
                            const next = { ...prev };
                            delete next.reference_number;
                            return next;
                          });
                        }
                      }}
                    />
                    {paymentErrors.reference_number && (
                      <p className="mt-1 text-sm text-red-600">{paymentErrors.reference_number}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Notes</label>
                    <textarea
                      rows={3}
                      className="input-field"
                      value={paymentForm.notes}
                      onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Voucher (PDF)</label>
                    <input
                      type="file"
                      accept="application/pdf,.pdf"
                      className="input-field"
                      onChange={(e) => {
                        const file = e.target.files && e.target.files[0];
                        if (!file) {
                          setPaymentForm({ ...paymentForm, broucher: null });
                          return;
                        }
                        const isPdf = file.type === 'application/pdf' || (file.name && file.name.toLowerCase().endsWith('.pdf'));
                        if (!isPdf) {
                          errorModal('Please upload a PDF file only.');
                          (e.target as HTMLInputElement).value = '';
                          setPaymentForm({ ...paymentForm, broucher: null });
                          return;
                        }
                        setPaymentForm({ ...paymentForm, broucher: file });
                      }}
                    />
                    <p className="mt-1 text-xs text-gray-500">Upload a PDF attachment for the payment.</p>
                  </div>

                  <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={handleClosePaymentModal}
                      className="btn-secondary w-full sm:w-auto"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-primary w-full sm:w-auto"
                    >
                      {isSubmitting ? 'Recording...' : 'Record Payment'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div >
        </div>
      )}

      {/* Vendor Progress Chart Modal */}
      <VendorProgressChart
        isOpen={showChartModal}
        onClose={() => {
          setShowChartModal(false);
          setSelectedInvoice(null);
        }}
        vendorId={selectedInvoice?.vendor_id || 0}
        vendorName={selectedInvoice?.vendor_name || ''}
      />

      {/* Attachment Viewer Modal */}
      {showAttachmentViewer && currentAttachmentUrl && (
        <div style={{ margin: '0px !important' }}>
          <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
            <div className="relative w-full max-w-6xl bg-white rounded-xl shadow-2xl overflow-hidden max-h-[95vh] flex flex-col">
              {/* Header */}
              <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <FileText className="h-5 w-5 text-white" />
                  <h3 className="text-xl font-semibold text-white truncate">
                    {currentAttachmentName}
                  </h3>
                </div>
                <div className="flex items-center space-x-2">
                  {originalAttachmentUrl && (
                    <button
                      onClick={() => {
                        window.open(originalAttachmentUrl, '_blank');
                      }}
                      className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                      title="Open in new tab"
                    >
                      <Eye className="h-5 w-5" />
                    </button>
                  )}
                  <a
                    href={currentAttachmentUrl}
                    download={currentAttachmentName}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                    title="Download"
                  >
                    <Download className="h-5 w-5" />
                  </a>
                  <button
                    onClick={closeAttachmentViewer}
                    className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                    title="Close"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Content - iframe */}
              <div className="flex-1 overflow-hidden bg-gray-100 p-4">
                {loadingAttachment ? (
                  <div className="flex items-center justify-center h-full">
                    <Spinner size="md" color="blue" />
                    <span className="ml-3 text-gray-600">Loading attachment...</span>
                  </div>
                ) : (
                  <iframe
                    src={currentAttachmentUrl}
                    className="w-full h-full min-h-[600px] rounded-lg shadow-lg border-0"
                    title={currentAttachmentName}
                    style={{ border: 'none' }}
                    onError={(e) => {
                      console.error('Iframe load error:', e);
                      errorModal('Failed to load in viewer. Opening in new tab...');
                      setTimeout(() => {
                        // If it's not a blob URL, open it
                        if (!currentAttachmentUrl.startsWith('blob:')) {
                          window.open(currentAttachmentUrl, '_blank');
                        }
                      }, 1000);
                    }}
                    onLoad={() => {
                      // Iframe loaded successfully
                      console.log('Iframe loaded successfully');
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      )}

    </div >
  );
};

export default Invoices; 